/* Gloomee Adoption Experience — reference-locked controller (FINAL_LOCKED_1TO1 handoff).

   Vanilla custom element, no dependencies. One persistent shell; a single active pane;
   explicit Continue/Back navigation only. Golden rule: hover/preview never mutates cart
   selection — only explicit clicks do.

   Scene engine (docs/03_SCENE_COORDINATE_ADDENDUM.md): per-step desktop/mobile configs with
   design-time pixel coordinates normalized against a configured artboard, anchor-based
   placement, state presets (size_jumbo / qty_2 / qty_2_with_companion / adopt_qty_2_glooma …)
   with graceful fallback to procedurally balanced compositions when no config exists.

   Cart: /cart/add.js with the sections param, then the theme's OWN CartDrawer.renderContents()
   so the drawer never flashes blank (it handles is-empty state, fixParsedHtml, close-button
   re-binding, and opening in the right order). Manual swap kept only as a fallback. */
(function () {
  if (customElements.get('gloomee-adoption-experience')) return;

  var PREFERS_REDUCED_MOTION = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // Narrow-width OR portrait: an upright iPad (e.g. 820x1180) is well past the 749.98px
  // width threshold on its own, but the desktop side-by-side layout (crew + cards sharing
  // one row) doesn't have room to breathe in a portrait-shaped viewport regardless of its
  // raw width — the mobile stacked layout reads better there. Landscape iPads stay desktop.
  var MOBILE_MQ = window.matchMedia ? window.matchMedia('(max-width: 749.98px), (orientation: portrait)') : { matches: false };
  var PANE_TRANSITION_FALLBACK_MS = 550;

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments;
      window.clearTimeout(t);
      t = window.setTimeout(function () { fn.apply(null, args); }, wait);
    };
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str == null ? '' : String(str);
    return div.innerHTML;
  }

  function clamp(n, min, max) {
    return Math.max(min, Math.min(max, n));
  }

  function toList(v) {
    if (Array.isArray(v)) return v;
    if (typeof v === 'string' && v.length) return v.split(',').map(function (s) { return s.trim(); });
    return [];
  }

  /* Anchor name → transform-origin offsets (fractions of the item box). */
  var ANCHORS = {
    'top-left': [0, 0], 'top-center': [0.5, 0], 'top-right': [1, 0],
    'center-left': [0, 0.5], 'center': [0.5, 0.5], 'center-right': [1, 0.5],
    'bottom-left': [0, 1], 'bottom-center': [0.5, 1], 'bottom-right': [1, 1],
  };

  // Shared between artDirectedSlots (actual render) and _applyWorstCaseSpacing (the
  // theoretical-maximum reservation) — must stay in sync, hence hoisted to module scope.
  var HERO_FILL = 0.62;

  var DEFAULT_ARTBOARDS = {
    desktop: { width: 1000, height: 760 },
    mobile: { width: 750, height: 620 },
  };

  /* Art-directed per-item slot presets (V6 scene addendum §8–9). Every count gets a composed
     arrangement — never an algorithmic cram into one bounding box. Each plush keeps its own
     slot (x / y / width / height / anchor / z), item size NEVER changes with count (the Size
     step's captured baseline is the single size authority), and a companion gets its own
     keychain-scale front-center slot instead of rescaling or displacing the mains.

     seat_width is the hard physical bound of the pedestal in the photo: when a preset's
     natural spread would overflow it, only the OFFSETS compress (the group tightens like a
     posed family photo) — the items themselves are untouchable. Even fully stacked the group
     is one item wide, so compression always terminates. */
  function artDirectedSlots(mainCount, companionScales, rewardScale, artboard, seatWidth, heroScale, spreadMultiplier) {
    var W = artboard.width;
    var H = artboard.height;
    var cx = W * 0.5;
    var baseY = H * 0.94;
    // Hero fill is seat-relative: at 100% one plush spans the full configured seat width —
    // as big as the pedestal physically allows — and smaller sizes scale down from there.
    // Because every step derives from the SAME seat_width and the SAME selected hero scale,
    // the item renders pixel-identical on every step automatically.
    var hero = clamp(typeof heroScale === 'number' && heroScale > 0 ? heroScale : 1, 0.3, 1);
    var itemW = seatWidth * hero * HERO_FILL;
    var itemH = itemW * 1.18;

    // Offsets in multiples of itemW/itemH. Depth per the addendum: qty 2 = back-left +
    // front-right; qty 3 = rear-center + front-left + front-right. Companions/reward front.
    var mains;
    if (mainCount <= 1) {
      mains = [{ dx: 0, dy: 0, z: 3 }];
    } else if (mainCount === 2) {
      mains = [
        { dx: -0.36, dy: -0.015, z: 2 },
        { dx: 0.36, dy: 0, z: 3 },
      ];
    } else {
      mains = [
        { dx: 0, dy: -0.05, z: 2 },
        { dx: -0.46, dy: 0, z: 3 },
        { dx: 0.46, dy: 0, z: 4 },
      ];
    }
    var companionCount = (companionScales || []).length;
    var comps = [];
    if (companionCount > 0) {
      var compOffsets = companionCount === 1 ? [0] : companionCount === 2 ? [-0.18, 0.2] : [-0.28, 0, 0.28];
      for (var c = 0; c < companionCount; c++) {
        // Companion scale is a fraction of the SEAT, not of the selected main. A companion
        // is its own physical product — Glooma is the same size whether it sits beside a
        // Large or a Giant — so its render must not inherit the main's hero scale.
        var cScale = clamp(companionScales[c] > 0 ? companionScales[c] : 0.44, 0.1, 1);
        var cw = seatWidth * cScale * HERO_FILL;
        comps.push({ dx: compOffsets[c] || 0, dy: 0.012, z: 8 + c, w: cw, h: cw * 1.22 });
      }
      if (mainCount === 1) {
        // One main + companion reads as a duo: main a touch left, companion front-right.
        mains[0].dx = -0.18;
        comps.forEach(function (cc) { cc.dx += 0.34; });
      } else {
        // Companion tucks in front-center — mains part slightly to make the pocket.
        mains.forEach(function (mm) { mm.dx *= 1.08; });
      }
    }
    // Free gift (reward) gets its own front slot beside the crew — front-left when the
    // companion pocket is center, mirroring reference 02's keychain placement.
    var reward = null;
    if (rewardScale > 0) {
      // Likewise seat-relative: the keychain is a fixed real object, not a proportion of
      // whichever main size happens to be selected.
      var rScale = clamp(rewardScale, 0.1, 1);
      var rw = seatWidth * rScale * HERO_FILL;
      reward = { dx: mainCount >= 2 ? -0.52 : 0.4, dy: 0.015, z: 7, w: rw, h: rw * 1.22 };
      if (companionCount > 0 && mainCount === 1) reward.dx = -0.44;
    }

    var spread = typeof spreadMultiplier === 'number' && spreadMultiplier > 0 ? spreadMultiplier : 1;

    var build = function (f) {
      var out = [];
      var i;
      for (i = 0; i < mains.length; i++) {
        out.push({
          label: 'main_' + (i + 1),
          x: cx + mains[i].dx * itemW * f,
          y: baseY + mains[i].dy * itemH,
          width: itemW,
          height: itemH,
          anchor: 'bottom-center',
          z_index: mains[i].z,
        });
      }
      for (i = 0; i < comps.length; i++) {
        out.push({
          label: 'companion_' + (i + 1),
          x: cx + comps[i].dx * itemW * f,
          y: baseY + comps[i].dy * itemH + H * 0.012,
          width: comps[i].w,
          height: comps[i].h,
          anchor: 'bottom-center',
          z_index: comps[i].z,
        });
      }
      if (reward) {
        out.push({
          label: 'reward_1',
          x: cx + reward.dx * itemW * f,
          y: baseY + reward.dy * itemH + H * 0.012,
          width: reward.w,
          height: reward.h,
          anchor: 'bottom-center',
          z_index: reward.z,
        });
      }
      return out;
    };
    // seat_width bounds the CONTACT FOOTPRINT, not the full silhouette: a plush's base is much
    // narrower than its drawn width (the ears/eyes flare out), and physically it's the feet
    // that must rest on the seat — bodies naturally overhang the edges, exactly like the V6
    // reference photos. Bounding the full silhouette instead collapses realistic pairs into a
    // single stack whenever the merchant's seat is close to one item's drawn width.
    var FOOTPRINT = 0.55;
    var spanOf = function (slots) {
      var left = Infinity;
      var right = -Infinity;
      slots.forEach(function (s) {
        left = Math.min(left, s.x - (s.width * FOOTPRINT) / 2);
        right = Math.max(right, s.x + (s.width * FOOTPRINT) / 2);
      });
      return right - left;
    };

    var slots = build(spread);
    if (seatWidth > 0 && spanOf(slots) > seatWidth) {
      var lo = 0;
      var hi = spread;
      for (var iter = 0; iter < 18; iter++) {
        var mid = (lo + hi) / 2;
        if (spanOf(build(mid)) > seatWidth) hi = mid; else lo = mid;
      }
      slots = build(lo);
    }

    // Recenter the composed group on the anchor point — asymmetric presets (1 main +
    // companion) would otherwise drift off the pedestal's center.
    var left = Infinity;
    var right = -Infinity;
    slots.forEach(function (s) {
      left = Math.min(left, s.x - s.width / 2);
      right = Math.max(right, s.x + s.width / 2);
    });
    var shift = cx - (left + right) / 2;
    slots.forEach(function (s) { s.x += shift; });
    return slots;
  }

  /* No-config fallback: same art direction against a sensible default pedestal width. */
  function proceduralSlots(content, artboard) {
    var compScales = content.companions.map(function (c) { return c.sceneScale || 0.44; });
    var rewardScale = content.reward ? content.reward.sceneScale : 0;
    return artDirectedSlots(content.mains, compScales, rewardScale, artboard, artboard.width * 0.55, content.heroScale, 1);
  }

  class GloomeeAdoptionExperience extends HTMLElement {
    connectedCallback() {
      if (this._gaxInitialized) return;
      this._gaxInitialized = true;

      this.shippingRuleMode = this.dataset.shippingRuleMode || 'non_large_sizes_free';
      this.shippingLabel = this.dataset.shippingLabel || 'Free shipping';
      this.largeShippingLabel = this.dataset.largeShippingLabel || '';
      this.discountStackModel = this.dataset.discountStackModel || 'sequential';
      this.showDetailedReceiptDiscounts = this.dataset.showDetailedReceiptDiscounts === 'true';
      this.showReceiptOriginalValue = this.dataset.showReceiptOriginalValue !== 'false';
      this.showReceiptSaleSavings = this.dataset.showReceiptSaleSavings !== 'false';
      this.showReceiptQuantitySavings = this.dataset.showReceiptQuantitySavings !== 'false';
      this.showReceiptGiftSavings = this.dataset.showReceiptGiftSavings !== 'false';
      this.showQuantityIncludesLine = this.dataset.showQuantityIncludesLine !== 'false';
      this.showOriginalPrices = this.dataset.showOriginalPrices !== 'false';
      this.showQuantityEachPrice = this.dataset.showQuantityEachPrice !== 'false';
      this.openCartAfterAdd = this.dataset.openCartAfterAdd === 'true';
      this.postAddBehavior = this.dataset.postAddBehavior || 'open_drawer';
      this.shippingSavingsCents = Math.round((parseFloat(this.dataset.shippingSavingsValue) || 0) * 100);
      this.animatedSteps = {
        size: this.dataset.animateSize === 'true',
        quantity: this.dataset.animateQuantity === 'true',
        companions: this.dataset.animateCompanions === 'true',
        adopt: this.dataset.animateAdopt === 'true',
      };
      this.checkoutUrl = this.dataset.checkoutUrl || '/checkout';
      this.dockBadges = {
        size: this.dataset.dockBadgeSize || '',
        quantity: this.dataset.dockBadgeQuantity || '',
        companions: this.dataset.dockBadgeCompanions || '',
        adopt: this.dataset.dockBadgeAdopt || '',
      };
      this.scrollToCartAfterAdd = this.dataset.scrollToCartAfterAdd === 'true';
      this.cartSuccessMessage = this.dataset.cartSuccessMessage || 'Added to cart!';
      this.analyticsEnabled = this.dataset.analyticsEnabled === 'true';
      this.analyticsNamespace = this.dataset.analyticsNamespace || 'gloomee_adoption';
      this.paneTransitionMs = parseInt(this.dataset.paneTransitionDuration, 10) || PANE_TRANSITION_FALLBACK_MS;
      this.allowBackNav = this.dataset.allowBackNavigation === 'true';
      this.cartAddUrl = this.dataset.cartAddUrl || '/cart/add.js';
      this.debugMode = this.dataset.debugMode === 'true';
      this.currencyCode = this.dataset.currencyCode || 'USD';

      this.isReducedMotion = PREFERS_REDUCED_MOTION;
      this.isEditorMode = !!(window.Shopify && window.Shopify.designMode);

      this.style.setProperty('--gax-pane-duration', this.paneTransitionMs + 'ms');
      if (this.isReducedMotion) this.classList.add('gax--reduced-motion');

      try {
        this._moneyFormatter = new Intl.NumberFormat(document.documentElement.lang || undefined, {
          style: 'currency',
          currency: this.currencyCode,
        });
      } catch (e) {
        this._moneyFormatter = null;
      }

      this.state = {
        activeStep: 'size',
        previewSizeKey: null,
        selectedSizeKey: null,
        selectedSizeMerchandise: null,
        selectedQuantity: 1,
        selectedUpsells: {},
        quantityDiscount: null,
        rewardSelection: null,
        forceFreeShipping: false,
        freeShipping: false,
        isTransitioning: false,
        subtotal: 0,
        compareSubtotal: 0,
        itemCount: 0,
      };

      this._viewedSteps = {};
      this._entered = {};

      this.panes = Array.prototype.slice.call(this.querySelectorAll('[data-gax-pane]'));
      this.stepOrder = this.panes.map(function (p) { return p.dataset.gaxPane; });

      this.sizeCards = Array.prototype.slice.call(this.querySelectorAll('[data-gax-size-card]'));
      this.quantityCards = Array.prototype.slice.call(this.querySelectorAll('[data-gax-quantity-card]'));
      this.companionCards = Array.prototype.slice.call(this.querySelectorAll('[data-gax-companion-card]'));

      this._preloadCutoutAspects();
      this._parseSceneConfigs();
      this._bindSelectionHandlers();
      this._bindDockAndProgress();
      this._measureChromeOffset();
      this._resizeHandler = debounce(this._onViewportChange.bind(this), 160);
      window.addEventListener('resize', this._resizeHandler);
      window.addEventListener('orientationchange', this._resizeHandler);

      // _handleFinalCta deliberately leaves the "Adopt the whole crew" button (and the
      // dock's copy of it) disabled/mid-loading through the navigation when redirecting
      // straight to checkout — intentional, so there's no flash of "done" and no chance of
      // a double submit while the browser is busy leaving. But hitting back from checkout
      // can restore this exact page from the browser's bfcache instead of reloading it —
      // a frozen snapshot of the DOM/JS heap exactly as it was at the moment of navigation,
      // which never re-runs connectedCallback or any other normal load logic. Without this,
      // the button comes back permanently stuck disabled, since nothing ever tells it the
      // submit that justified disabling it never actually happened on THIS page instance.
      this._pageshowHandler = this._onPageShow.bind(this);
      window.addEventListener('pageshow', this._pageshowHandler);

      this._initDefaultSelectionPreview();
      this._activatePane('size', { initial: true });
      this._recomputeDerived();
      this._renderAllScenes();
      this._dispatchAnalytics('gloomee_step_view', { step: 'size' });

      if (this.debugMode) this._setupDebugPanel();
      this._setupThemeEditorHooks();
    }

    disconnectedCallback() {
      if (this._snapCleanup) this._snapCleanup.forEach(function (fn) { fn(); });
      if (this._resizeHandler) {
        window.removeEventListener('resize', this._resizeHandler);
        window.removeEventListener('orientationchange', this._resizeHandler);
      }
      if (this._pageshowHandler) window.removeEventListener('pageshow', this._pageshowHandler);
    }

    // event.persisted is true only when the page was restored from bfcache (e.g. hitting
    // back after being redirected to checkout) rather than freshly loaded/reloaded. Only
    // the clicked button gets dataset.state = 'loading' in _handleFinalCta — its sibling
    // (dock vs. final CTA, whichever wasn't clicked) is disabled as a side effect without
    // that marker, so gating the reset on dataset.state would leave it stuck. Unconditional
    // is safe here: a normal, correctly-enabled button just gets disabled=false again,
    // a no-op either way.
    _onPageShow(event) {
      if (!event.persisted) return;
      var finalBtn = this.querySelector('[data-gax-final-cta]');
      var dockBtn = this.querySelector('[data-gax-continue]');
      [finalBtn, dockBtn].forEach(function (btn) {
        if (!btn) return;
        btn.dataset.state = '';
        btn.disabled = false;
      });
      this._undoLastCheckoutAdd();
    }

    // Restoring from bfcache after a checkout redirect means the customer abandoned that
    // checkout — re-enabling the button (above) makes it possible to hit "Adopt the whole
    // crew" again, which would otherwise add the exact same items on top of what checkout
    // already confirmed (/cart/add.js is additive), doubling every quantity. Decrements
    // instead of zeroing/clearing the whole cart: only undoes what THIS add contributed, so
    // anything already in the cart for an unrelated reason (same variant added elsewhere,
    // before this widget ran) is left alone.
    _undoLastCheckoutAdd() {
      var items = this._lastCheckoutItems;
      if (!items || !items.length) return;
      this._lastCheckoutItems = null;

      fetch('/cart.js')
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var updates = {};
          items.forEach(function (item) {
            var line = cart.items.filter(function (li) { return li.variant_id === item.id; })[0];
            var currentQty = line ? line.quantity : 0;
            updates[item.id] = Math.max(0, currentQty - item.quantity);
          });
          return fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ updates: updates }),
          });
        })
        .catch(function () {
          // Best-effort: these are plain, standard cart AJAX endpoints, not expected to
          // fail in normal use — if one somehow does, the duplicate quantities from the
          // abandoned checkout are still sitting in the cart rather than silently lost, so
          // nothing here can under-charge; a customer who then completes checkout without
          // reviewing their cart could still be charged for the doubled amount.
        });
    }

    _measureChromeOffset() {
      // Distance from document top to this section = merchant theme's header chrome.
      // The shell subtracts it so the experience fills exactly one viewport.
      var rect = this.getBoundingClientRect();
      var top = Math.max(0, Math.round(rect.top + window.scrollY));
      this.style.setProperty('--gax-top', top + 'px');
    }

    _onViewportChange() {
      this._measureChromeOffset();
      this._renderAllScenes();
    }

    _initDefaultSelectionPreview() {
      // Preview state only (never mutates cart selection): the recommended size seeds the
      // opening scene so the pedestal is never empty.
      if (!this.sizeCards.length) return;
      var recommendedKey = this.dataset.defaultRecommendedSize;
      var card = this.sizeCards.filter(function (c) { return c.dataset.sizeKey === recommendedKey; })[0] || this.sizeCards[0];
      this.state.previewSizeKey = card.dataset.sizeKey;
    }

    /* ---------------- pane navigation ---------------- */

    _paneEl(step) {
      return this.panes.filter(function (p) { return p.dataset.gaxPane === step; })[0] || null;
    }

    _stepIndex(step) {
      return this.stepOrder.indexOf(step);
    }

    _nextStep(from) {
      var idx = this._stepIndex(from);
      return this.stepOrder[idx + 1] || null;
    }

    _prevStep(from) {
      var idx = this._stepIndex(from);
      return this.stepOrder[idx - 1] || null;
    }

    _canContinueFrom(step) {
      if (step === 'size') return !!this.state.selectedSizeKey;
      return true;
    }

    _activatePane(step, opts) {
      opts = opts || {};
      var target = this._paneEl(step);
      if (!target) return;
      var current = this.panes.filter(function (p) { return p.classList.contains('is-active'); })[0];
      var direction = opts.direction || 'forward';

      this.panes.forEach(function (p) {
        p.setAttribute('aria-hidden', p === target ? 'false' : 'true');
        if (p !== target) {
          p.setAttribute('inert', '');
        } else {
          p.removeAttribute('inert');
        }
      });

      if (current && current !== target && !opts.initial) {
        current.dataset.leaveDirection = direction === 'forward' ? 'forward' : 'back';
        current.classList.remove('is-active');
      }

      // Clear any stale exit transform before re-entry (historic clipped-pane bug guard).
      delete target.dataset.leaveDirection;
      target.dataset.enterDirection = direction === 'forward' ? 'forward' : 'back';
      if (!opts.initial) void target.offsetWidth;
      target.classList.add('is-active');

      this.state.activeStep = step;
      this._playEntranceIfNeeded(step);
      this._updateProgress();
      this._updateDock();
      // The receipt's content must be in place BEFORE _renderScene runs: it measures the
      // receipt's real height (to size/position it against the crew) as part of that same
      // call, and _updateSummary is what fills in the receipt's actual line items. Calling
      // it after, as before, meant that first measurement read whatever stale content
      // (or none) was already there, computing a wrong size that visibly corrected itself
      // a frame later once _deferPinLedger's re-check saw the real content.
      if (step === 'adopt') this._updateSummary();
      this._renderScene(step);
      this._deferPinLedger(target, step);

      var heading = target.querySelector('.gax__pane-title');
      if (heading && !opts.initial) {
        window.setTimeout(function () { heading.focus({ preventScroll: true }); }, this.paneTransitionMs * 0.6);
      }

      if (!this._viewedSteps[step]) {
        this._viewedSteps[step] = true;
        this._dispatchAnalytics('gloomee_step_view', { step: step });
      }
    }

    _goToStep(step, direction) {
      if (this.state.isTransitioning || !step || step === this.state.activeStep) return;
      if (direction === 'forward' && !this._canContinueFrom(this.state.activeStep)) return;

      this.state.isTransitioning = true;
      this._activatePane(step, { direction: direction });

      this._dispatchAnalytics(direction === 'forward' ? 'gloomee_step_continue' : 'gloomee_step_back', {
        step: step,
      });

      var self = this;
      window.setTimeout(function () {
        self.state.isTransitioning = false;
      }, this.paneTransitionMs + 40);
    }

    /* ---------------- entrance motion ----------------
       Two modes. Default: a one-time subtle card rise+fade (unchanged behaviour).
       Scene-building (per-pane toggle): a staged choreography that replays every time the
       pane is entered — heading, then the scene note, then the cards cascading in, and on
       Adopt the receipt building to a final reveal. The Gloomees themselves are
       deliberately NOT part of it: they persist across steps and glide to their new slots
       (see _reconcileSceneItems), so the experience builds AROUND the crew you've chosen. */

    _playEntranceIfNeeded(step) {
      if (!this.isReducedMotion && this.animatedSteps && this.animatedSteps[step]) {
        this._playChoreography(step);
        return;
      }
      if (this._entered[step]) return;
      this._entered[step] = true;

      var deck = this.querySelector('[data-gax-deck="' + step + '"]');
      if (!deck) return;
      var cards = Array.prototype.slice.call(deck.querySelectorAll(':scope > .gax__card'));
      cards.forEach(function (card, i) {
        card.style.setProperty('--card-delay', (i * 70) + 'ms');
        card.classList.add('gax-enter');
      });
    }

    _playChoreography(step) {
      var pane = this._paneEl(step);
      if (!pane) return;

      // Cards cascade after the heading has landed; the receipt cascades a touch later
      // still so the Adopt step reads as one build rather than two competing ones.
      var CARD_BASE = 260;
      var deck = this.querySelector('[data-gax-deck="' + step + '"]');
      if (deck) {
        Array.prototype.slice.call(deck.querySelectorAll(':scope > .gax__card')).forEach(function (card, i) {
          card.style.setProperty('--card-delay', (CARD_BASE + i * 90) + 'ms');
          card.classList.remove('gax-enter');
        });
      }
      // Receipt rows stagger via CSS nth-child rather than JS custom properties: the
      // summary re-renders its innerHTML after this runs, which would wipe inline styles.

      // Restart the animation: strip the class, force a reflow, re-add. Without the
      // reflow the browser coalesces both mutations and nothing replays.
      pane.classList.add('gax-anim');
      pane.classList.remove('is-choreo');
      void pane.offsetWidth;
      pane.classList.add('is-choreo');
    }

    /* ---------------- selection handlers ---------------- */

    _bindSelectionHandlers() {
      var self = this;

      this.sizeCards.forEach(function (card) {
        card.addEventListener('click', function () { self.selectSize(card); });
      });

      this.quantityCards.forEach(function (card) {
        card.addEventListener('click', function () { self.selectQuantity(card); });
      });

      this.companionCards.forEach(function (card) {
        card.addEventListener('click', function () { self.toggleCompanion(card); });
      });

      var replayBtn = this.querySelector('[data-gax-replay]');
      if (replayBtn) {
        replayBtn.addEventListener('click', function () { self._replaySizeVideo(); });
      }

      var finalBtn = this.querySelector('[data-gax-final-cta]');
      if (finalBtn) {
        finalBtn.addEventListener('click', function () {
          self._dispatchAnalytics('gloomee_final_cta_click', self._buildAnalyticsSnapshot());
          self._handleFinalCta();
        });
      }

    }

    _bindDockAndProgress() {
      var self = this;
      var continueBtn = this.querySelector('[data-gax-continue]');
      if (continueBtn) {
        continueBtn.addEventListener('click', function () {
          if (self.state.activeStep === 'adopt') {
            self._dispatchAnalytics('gloomee_final_cta_click', self._buildAnalyticsSnapshot());
            self._handleFinalCta();
            return;
          }
          var next = self._nextStep(self.state.activeStep);
          if (next) self._goToStep(next, 'forward');
        });
      }

      var backBtn = this.querySelector('[data-gax-back]');
      if (backBtn) {
        backBtn.addEventListener('click', function () {
          var prev = self._prevStep(self.state.activeStep);
          if (prev) self._goToStep(prev, 'back');
        });
      }

      var tabs = Array.prototype.slice.call(this.querySelectorAll('[data-gax-step-tab]'));
      tabs.forEach(function (tab) {
        tab.addEventListener('click', function () {
          if (!self.allowBackNav) return;
          var step = tab.dataset.gaxStepTab;
          var stepIdx = self._stepIndex(step);
          var currentIdx = self._stepIndex(self.state.activeStep);
          if (stepIdx < currentIdx) self._goToStep(step, 'back');
        });
      });
    }

    _setCardCtaLabel(cardEl, selected) {
      var label = cardEl.querySelector('[data-gax-cta-label]');
      if (!label) return;
      var text = selected ? label.dataset.ctaSelected : label.dataset.ctaDefault;
      if (text) label.textContent = selected ? '✓ ' + text : text;
    }

    selectSize(cardEl) {
      if (cardEl.dataset.available === 'false') return;

      this.sizeCards.forEach(function (c) {
        c.classList.remove('is-selected');
        c.setAttribute('aria-selected', 'false');
      });
      cardEl.classList.add('is-selected');
      cardEl.setAttribute('aria-selected', 'true');
      this.sizeCards.forEach(function (c) { this._setCardCtaLabel(c, c === cardEl); }, this);

      this.state.selectedSizeKey = cardEl.dataset.sizeKey;
      this.state.previewSizeKey = cardEl.dataset.sizeKey;
      this.state.selectedSizeMerchandise = {
        variantId: cardEl.dataset.variantId,
        price: parseInt(cardEl.dataset.price, 10) || 0,
        comparePrice: parseInt(cardEl.dataset.comparePrice, 10) || 0,
        originalPrice: parseInt(cardEl.dataset.originalPrice, 10) || parseInt(cardEl.dataset.price, 10) || 0,
        saleActive: cardEl.dataset.saleActive === 'true',
        title: cardEl.dataset.title || '',
        isLarge: cardEl.dataset.isLarge === 'true',
        badgeText: cardEl.dataset.badgeText || '',
        recommended: cardEl.dataset.recommended === 'true',
        cutout: cardEl.dataset.cutout || '',
        heroScale: clamp((parseFloat(cardEl.dataset.heroScale) || 100) / 100, 0.3, 1),
      };

      this._updateQuantityCardPricing();
      this._recomputeDerived();
      this._renderAllScenes();
      this._maybePlaySizeVideo(cardEl);
      this._dispatchAnalytics('gloomee_size_select', {
        sizeKey: this.state.selectedSizeKey,
        variantId: this.state.selectedSizeMerchandise.variantId,
        price: this.state.selectedSizeMerchandise.price,
        source: 'select_card',
      });
    }

    selectQuantity(cardEl) {
      this.quantityCards.forEach(function (c) {
        c.classList.remove('is-selected');
        c.setAttribute('aria-selected', 'false');
      });
      cardEl.classList.add('is-selected');
      cardEl.setAttribute('aria-selected', 'true');

      this.state.selectedQuantity = parseInt(cardEl.dataset.quantityValue, 10) || 1;
      this.state.forceFreeShipping = cardEl.dataset.forceFreeShipping === 'true';

      if (cardEl.dataset.discountEnabled === 'true') {
        this.state.quantityDiscount = {
          enabled: true,
          type: cardEl.dataset.discountType || 'none',
          value: parseFloat(cardEl.dataset.discountValue) || 0,
          label: cardEl.dataset.discountLabel || '',
          badge: cardEl.dataset.discountBadge || '',
        };
      } else {
        this.state.quantityDiscount = null;
      }

      var rewardType = cardEl.dataset.rewardType;
      var rewardCommon = {
        title: cardEl.dataset.rewardLabel || 'Gift',
        cutout: cardEl.dataset.rewardImage || '',
        sceneScale: clamp((parseFloat(cardEl.dataset.rewardSceneScale) || 38) / 100, 0.1, 1),
        discountPercent: cardEl.dataset.rewardDiscountPercent != null
          ? clamp(parseFloat(cardEl.dataset.rewardDiscountPercent) || 0, 0, 100)
          : 100,
        badgeText: cardEl.dataset.rewardBadgeText || '',
      };
      if (rewardType === 'add_product' && cardEl.dataset.rewardVariantId) {
        this.state.rewardSelection = Object.assign({
          variantId: cardEl.dataset.rewardVariantId,
          price: parseInt(cardEl.dataset.rewardPrice, 10) || 0,
        }, rewardCommon);
        this._dispatchAnalytics('gloomee_reward_unlocked', { title: this.state.rewardSelection.title });
      } else if (rewardType === 'visual_only' && cardEl.dataset.rewardImage) {
        // Visual-only gift: rendered into the scene but never added as a cart line.
        this.state.rewardSelection = Object.assign({ variantId: null, price: 0 }, rewardCommon);
      } else {
        this.state.rewardSelection = null;
      }

      this._recomputeDerived();
      this._renderAllScenes();
      this._dispatchAnalytics('gloomee_quantity_select', { quantity: this.state.selectedQuantity, source: 'select_card' });
    }

    toggleCompanion(cardEl) {
      var blockId = cardEl.dataset.blockId;
      if (cardEl.dataset.available === 'false') return;

      if (this.state.selectedUpsells[blockId]) {
        delete this.state.selectedUpsells[blockId];
        cardEl.classList.remove('is-added');
        cardEl.setAttribute('aria-selected', 'false');
        this._setCardCtaLabel(cardEl, false);
        this._dispatchAnalytics('gloomee_upsell_remove', { blockId: blockId, title: cardEl.dataset.title });
      } else {
        this.state.selectedUpsells[blockId] = {
          variantId: cardEl.dataset.variantId,
          price: parseInt(cardEl.dataset.price, 10) || 0,
          comparePrice: parseInt(cardEl.dataset.comparePrice, 10) || 0,
          originalPrice: parseInt(cardEl.dataset.originalPrice, 10) || parseInt(cardEl.dataset.price, 10) || 0,
          saleActive: cardEl.dataset.saleActive === 'true',
          title: cardEl.dataset.title || '',
          characterKey: cardEl.dataset.characterKey || '',
          cutout: cardEl.dataset.cutout || '',
          sceneScale: clamp((parseFloat(cardEl.dataset.sceneScale) || 44) / 100, 0.1, 1),
        };
        cardEl.classList.add('is-added');
        cardEl.setAttribute('aria-selected', 'true');
        this._setCardCtaLabel(cardEl, true);
        this._dispatchAnalytics('gloomee_upsell_select', {
          blockId: blockId,
          variantId: cardEl.dataset.variantId,
          title: cardEl.dataset.title,
          price: parseInt(cardEl.dataset.price, 10) || 0,
          source: 'select_card',
        });
      }

      this._recomputeDerived();
      this._renderAllScenes();
    }

    /* ---------------- scene engine ---------------- */

    _parseSceneConfigs() {
      this.sceneConfigs = {};
      var self = this;
      Array.prototype.slice.call(this.querySelectorAll('[data-gax-scene-config]')).forEach(function (node) {
        var step = node.dataset.gaxSceneConfig;
        try {
          self.sceneConfigs[step] = JSON.parse(node.textContent);
        } catch (e) {
          self.sceneConfigs[step] = null;
          if (self.debugMode) console.warn('[gax] invalid scene JSON for step', step, e);
        }
      });
    }

    _sceneBreakpoint() {
      return MOBILE_MQ.matches ? 'mobile' : 'desktop';
    }

    _sceneStateCandidates(step) {
      var s = this.state;
      var q = clamp(s.selectedQuantity || 1, 1, 3);
      var sizeKey = s.selectedSizeKey || s.previewSizeKey || 'jumbo';
      var upsellKeys = Object.keys(s.selectedUpsells);
      var hasCompanion = upsellKeys.length > 0;
      var charKey = '';
      if (upsellKeys.length === 1) {
        charKey = (s.selectedUpsells[upsellKeys[0]].characterKey || '').toLowerCase().replace(/[^a-z0-9_]/g, '');
      }

      if (step === 'size') return ['size_' + sizeKey];
      if (step === 'quantity') return ['qty_' + q];
      if (step === 'companions') {
        return hasCompanion
          ? ['qty_' + q + '_with_companion', 'qty_' + q]
          : ['qty_' + q + '_no_companion', 'qty_' + q];
      }
      // adopt: dedicated final compositions first
      var out = [];
      if (charKey) out.push('adopt_qty_' + q + '_' + charKey);
      out.push('adopt_qty_' + q);
      if (hasCompanion) out.push('qty_' + q + '_with_companion');
      out.push('qty_' + q);
      return out;
    }

    _sceneContent(step) {
      // What actually stands in the scene for this step/state.
      var s = this.state;
      var q = clamp(s.selectedQuantity || 1, 1, 3);
      var mains = step === 'size' ? 1 : q;
      var companions = [];
      if (step === 'companions' || step === 'adopt') {
        Object.keys(s.selectedUpsells).forEach(function (k) { companions.push(s.selectedUpsells[k]); });
      }
      // The free gift joins the scene on every step after it's earned (quantity onward).
      var reward = null;
      if (step !== 'size' && s.rewardSelection && s.rewardSelection.cutout) {
        reward = s.rewardSelection;
      }
      var mainCutout = '';
      var heroScale = 1;
      if (s.selectedSizeMerchandise && s.selectedSizeMerchandise.cutout) {
        mainCutout = s.selectedSizeMerchandise.cutout;
        heroScale = s.selectedSizeMerchandise.heroScale || 1;
      } else {
        var previewCard = this.sizeCards.filter(function (c) { return c.dataset.sizeKey === s.previewSizeKey; })[0] || this.sizeCards[0];
        if (previewCard) {
          mainCutout = previewCard.dataset.cutout || '';
          heroScale = clamp((parseFloat(previewCard.dataset.heroScale) || 100) / 100, 0.3, 1);
        }
      }
      return { mains: mains, mainCutout: mainCutout, companions: companions, reward: reward, heroScale: heroScale };
    }

    _resolveSlots(step, config, artboard, content, currentScale) {
      // seat_width takes priority when set: one number replaces hand-authored per-count slots
      // entirely. The selected size's hero fill is seat-relative (100% = one plush spans the
      // whole seat), each companion and the free gift carry their own merchant-set scene
      // scales, and every step derives from the same numbers — so sizes stay identical across
      // steps by construction.
      if (config && config.seat_width > 0) {
        var compScales = content.companions.map(function (c) { return c.sceneScale || 0.44; });
        var rewardScale = content.reward ? content.reward.sceneScale : 0;
        return artDirectedSlots(content.mains, compScales, rewardScale, artboard, config.seat_width, content.heroScale, config.spread || config.cluster_overlap);
      }
      var candidates = this._sceneStateCandidates(step);
      var slots = config && Array.isArray(config.slots) ? config.slots : null;
      if (slots && slots.length) {
        for (var i = 0; i < candidates.length; i++) {
          var key = candidates[i];
          var matched = slots.filter(function (slot) {
            return toList(slot.applies_to).indexOf(key) > -1;
          });
          if (matched.length) return matched;
        }
        var generic = slots.filter(function (slot) { return toList(slot.applies_to).length === 0; });
        if (generic.length) return generic;
      }
      return proceduralSlots(content, artboard);
    }

    _renderAllScenes() {
      var self = this;
      this.stepOrder.forEach(function (step) { self._renderScene(step); });
      var activeStep = this.state.activeStep;
      var activePane = activeStep && this._paneEl(activeStep);
      if (activePane) this._deferPinLedger(activePane, activeStep);
    }


    _projectBackgroundAnchor(step, root, bp) {
      var config = this.sceneConfigs[step] && this.sceneConfigs[step][bp];
      var anchor = config && config.background_anchor;
      if (!anchor || typeof anchor.x !== 'number' || typeof anchor.y !== 'number') return null;

      var bgImg = root.querySelector('.gax-scene__bg--' + bp);
      if (!bgImg) return null;
      if (!bgImg.complete || !bgImg.naturalWidth) {
        this._watchBackgroundLoad(bgImg, step);
        return null;
      }

      var boxRect = root.getBoundingClientRect(); // .gax-scene is inset:0 on the pane
      if (boxRect.width <= 0 || boxRect.height <= 0) return null;

      var imgW = bgImg.naturalWidth;
      var imgH = bgImg.naturalHeight;
      var scale = Math.max(boxRect.width / imgW, boxRect.height / imgH);
      var dispW = imgW * scale;
      var dispH = imgH * scale;

      // Bias the cover-crop window to be centered AROUND THE ANCHOR rather than around the
      // photo's geometric center (the CSS default). This is what keeps décor near the anchor
      // (candle, books) consistently in view as the viewport's aspect ratio changes — the
      // whole crop now follows the subject instead of an arbitrary fixed point. Applied to the
      // actual <img> via object-position so what's computed here is exactly what renders.
      var posX = clamp(anchor.x, 0, 1);
      var posY = clamp(anchor.y, 0, 1);
      bgImg.style.objectPosition = (posX * 100).toFixed(2) + '% ' + (posY * 100).toFixed(2) + '%';

      var offsetX = (boxRect.width - dispW) * posX;
      var offsetY = (boxRect.height - dispH) * posY;

      return {
        x: offsetX + anchor.x * dispW,
        y: offsetY + anchor.y * dispH,
        scale: scale,
      };
    }

    _renderAnchorDebugMarker(root, projected) {
      // Debug-mode-only calibration aid: a crosshair drawn at the exact point the anchor math
      // is currently projecting to, so a merchant can visually confirm/adjust background_anchor
      // against their real photo instead of guessing from screenshots or raw pixel math.
      var marker = root.querySelector(':scope > .gax-scene__anchor-debug');
      if (!marker) {
        marker = document.createElement('div');
        marker.className = 'gax-scene__anchor-debug';
        marker.innerHTML =
          '<span class="gax-scene__anchor-debug-h"></span>' +
          '<span class="gax-scene__anchor-debug-v"></span>' +
          '<span class="gax-scene__anchor-debug-dot"></span>' +
          '<span class="gax-scene__anchor-debug-label"></span>';
        root.appendChild(marker);
      }
      marker.style.left = projected.x + 'px';
      marker.style.top = projected.y + 'px';
      marker.querySelector('.gax-scene__anchor-debug-label').textContent =
        'anchor: ' + Math.round(projected.x) + ', ' + Math.round(projected.y);
    }

    _clearAnchorDebugMarker(root) {
      var marker = root.querySelector(':scope > .gax-scene__anchor-debug');
      if (marker) marker.remove();
    }

    _watchBackgroundLoad(bgImg, step) {
      if (bgImg.dataset.gaxLoadWatched) return;
      bgImg.dataset.gaxLoadWatched = 'true';
      var self = this;
      bgImg.addEventListener('load', function () { self._renderScene(step); }, { once: true });
    }

    _renderScene(step) {
      var root = this.querySelector('[data-gax-scene-root="' + step + '"]');
      if (!root) return;
      var stageEl = root.querySelector('[data-gax-artboard]');
      if (!stageEl) return;

      // The stage is a pure bounding rect (see CSS). Contain-fit the real artboard inside it
      // in JS so the configured aspect ratio is always pixel-exact — never distorted by a
      // CSS max-height/width cap fighting the aspect-ratio property.
      var artboardEl = stageEl.querySelector(':scope > .gax-scene__artboard-inner');
      if (!artboardEl) {
        artboardEl = document.createElement('div');
        artboardEl.className = 'gax-scene__artboard-inner';
        var existingProducts = stageEl.querySelector('[data-gax-scene-products]');
        var existingAnnotations = stageEl.querySelector('[data-gax-scene-annotations]');
        if (existingProducts) artboardEl.appendChild(existingProducts);
        if (existingAnnotations) artboardEl.appendChild(existingAnnotations);
        stageEl.appendChild(artboardEl);
      }
      var productsEl = artboardEl.querySelector('[data-gax-scene-products]');
      var annotationsEl = artboardEl.querySelector('[data-gax-scene-annotations]');
      if (!productsEl) return;

      var bp = this._sceneBreakpoint();
      var config = (this.sceneConfigs[step] && this.sceneConfigs[step][bp]) || null;
      var artboard = (config && config.artboard && config.artboard.width > 0 && config.artboard.height > 0)
        ? config.artboard
        : DEFAULT_ARTBOARDS[bp];

      // Photo-anchored positioning: if the merchant gave us background_anchor (a fraction of
      // THEIR OWN photo's pixel space, matching the point where the pedestal actually sits),
      // project that point through the real object-fit:cover transform of the loaded
      // background image and place the artboard there — instead of a fixed CSS box that has
      // no idea how cover cropped the photo. This is what actually keeps the cutout glued to
      // the pedestal regardless of viewport size or the photo's own aspect ratio.
      var projected = this._projectBackgroundAnchor(step, root, bp);
      if (projected) {
        stageEl.style.left = '0';
        stageEl.style.top = '0';
        stageEl.style.right = 'auto';
        stageEl.style.bottom = 'auto';
        stageEl.style.width = '100%';
        stageEl.style.height = '100%';
        artboardEl.style.position = 'absolute';
        var artW = artboard.width * projected.scale;
        var artH = artboard.height * projected.scale;
        // Safety cap: never let the artboard (and whatever plush is rendering inside it —
        // sizes vary, Giant is much bigger than Large) grow wide enough to reach into the
        // card column on desktop. Also never let it reach ABOVE the heading — this bounds the
        // WHOLE scene (the plush itself, not just the annotation on top of it), which is the
        // only way to guarantee the product can't visually collide with the heading text either.
        // Both caps shrink width+height together, so aspect ratio and the resting-point anchor
        // (bottom-anchored at projected.y, which never moves) stay exact — it just renders
        // smaller, never distorted or repositioned.
        var paneRect = root.getBoundingClientRect();
        var maxArtW = paneRect.width * (bp === 'mobile' ? 0.86 : 0.54);

        // Width cap only — no shrinking to chase header/card/deck clearance. The merchant's
        // X/Y anchor is the priority position; header/cards/deck adapt to wherever the crew
        // ends up, never the other way around. This cap only keeps a huge configured
        // seat_width from reaching into the card column on desktop.
        var wShrink;
        if (bp === 'mobile') {
          wShrink = Math.min(1, maxArtW / artW);
        } else {
          // Desktop: a pure width cap (paneRect.width * 0.54) ignores the pane's own HEIGHT
          // entirely. For this merchant's large seat_width, that cap is ALWAYS what binds —
          // the natural, cover-fit-scaled size (artW before any cap) always exceeds it — so
          // pane height, which is what actually drives projected.scale on a typical
          // wide/short desktop pane, had no say in the crew's final size at all: the exact
          // same width at two different heights rendered the exact same absolute crew size,
          // even though the photo itself displayed at two very different sizes. The crew
          // then reads as proportionally bigger or smaller against the photo as the pane
          // gets shorter or taller — the same "moving relative to the background" the ghost/
          // anchor math elsewhere in this function is otherwise built to prevent.
          // DESKTOP_NATURAL_FRACTION caps the crew at a fixed fraction of its OWN natural
          // (cover-fit-scaled) size instead of a fraction of pane width — calibrated to
          // reproduce this cap's existing result at a representative desktop size (1536x1024,
          // a MacBook's default CSS viewport) so today's look is unchanged there. Because
          // both the crew's natural size and this fraction of it move with the SAME
          // projected.scale that sizes the photo, the shrunk crew stays a constant
          // proportion of the photo across every aspect ratio instead of just that one. The
          // width cap remains as an outer safety net (whichever constraint is tighter wins)
          // for genuinely narrow panes — tablet portrait — where even that fraction would
          // still reach into the card column.
          var DESKTOP_NATURAL_FRACTION = 0.4687;
          wShrink = Math.min(DESKTOP_NATURAL_FRACTION, maxArtW / artW);
          // Third constraint, height this time: for a large configured seat_width, a
          // single item's own natural height (seat_width * hero * HERO_FILL * 1.18, before
          // any of the caps above) can already exceed the artboard's own configured
          // height — so even a "proportionally correct" shrink can still leave the
          // worst-case item (3 mains, rear-center, reaching highest) poking above the
          // pane's own top edge on a short enough pane, since projected.y (the fixed
          // bottom-anchor point) has less room above it to work with there. Solves for
          // the largest artW that keeps that worst-case item's top at or below
          // projected.y minus a small margin, and folds it in as one more "whichever is
          // tightest wins" floor alongside the width and natural-fraction caps above.
          if (config && config.seat_width > 0) {
            var HEIGHT_SAFETY_MARGIN = 20;
            var worstItemHUnits = config.seat_width * this._maxHeroScale() * HERO_FILL * 1.18;
            // 1.05x: the rear-center slot (main_1 of 3) sits dy: -0.05 * itemH above the
            // shared baseline, so it reaches 5% of its own height higher than a lone item
            // would — using this worst case regardless of how many mains this step
            // actually shows keeps the cap stable across steps, same reasoning as the
            // ghost elsewhere in this function.
            var maxArtWForHeight = ((projected.y - HEIGHT_SAFETY_MARGIN) * artboard.width) / (1.05 * worstItemHUnits);
            if (maxArtWForHeight > 0) {
              wShrink = Math.min(wShrink, maxArtWForHeight / artW);
            }
          }
        }
        if (wShrink < 1) { artW *= wShrink; artH *= wShrink; }

        var usingSeatWidthLayout = !!(config && config.seat_width > 0);
        var singleTallGhost = step === 'size';

        artboardEl.style.width = artW + 'px';
        artboardEl.style.height = artH + 'px';
        artboardEl.style.left = (projected.x - artW * 0.5) + 'px';
        var ghostEl = this._updateGhostSlots(artboardEl, usingSeatWidthLayout ? config : null, artboard, singleTallGhost);

        // The crew's X/Y anchor is absolute on both breakpoints — never pushed or shrunk for
        // the header or the cards/deck/receipt. Those adapt to the crew's worst case instead
        // (header shrinks proportionally rather than clipping or pushing the crew; cards/deck
        // size around it) — see _applyWorstCaseSpacing. Last-resort safety only: never let the
        // anchor place the resting point fully off the pane.
        if (projected.y < 0) projected.y = 0;
        if (projected.y > paneRect.height) projected.y = paneRect.height;

        artboardEl.style.top = (projected.y - artH * 0.94) + 'px';
        if (this.debugMode) this._renderAnchorDebugMarker(root, projected);

        // Final pass: with the resting point settled, measure the (still invisible) worst-case
        // ghost one more time — this is the real, on-screen reservation the header/cards/deck
        // will respect.
        if (ghostEl && ghostEl.children.length) {
          var ghostRectsFinal = Array.prototype.map.call(ghostEl.children, function (c) { return c.getBoundingClientRect(); });
          this._worstCaseRects = this._worstCaseRects || {};
          this._worstCaseRects[step] = { rects: ghostRectsFinal, paneRect: paneRect };
        } else {
          if (this._worstCaseRects) delete this._worstCaseRects[step];
        }
        // The EFFECTIVE scale actually rendered, after the width cap above may have shrunk it —
        // NOT projected.scale directly, which is the pre-cap value. Using the wrong one here
        // silently under/over-sizes anything (like the cross-step baseline) that relies on this
        // number to convert real px back into this step's artboard-unit space.
        var effectiveScale = artW / artboard.width;
      } else {
        var resetBg = root.querySelector('.gax-scene__bg--' + bp);
        if (resetBg) resetBg.style.objectPosition = '';
        this._clearAnchorDebugMarker(root);
        stageEl.style.left = '';
        stageEl.style.top = '';
        stageEl.style.right = '';
        stageEl.style.bottom = '';
        stageEl.style.width = '';
        stageEl.style.height = '';
        artboardEl.style.position = '';
        artboardEl.style.left = '';
        artboardEl.style.top = '';
        var stageRect = stageEl.getBoundingClientRect();
        if (stageRect.width > 0 && stageRect.height > 0) {
          var scale = Math.min(stageRect.width / artboard.width, stageRect.height / artboard.height);
          artboardEl.style.width = (artboard.width * scale) + 'px';
          artboardEl.style.height = (artboard.height * scale) + 'px';
        }
      }

      // The single conversion factor between "artboard units" and real on-screen px for THIS
      // step's current render — whichever branch above ran. This is what lets a pixel size
      // measured on one step's photo be translated exactly onto a completely different step's
      // photo (different resolution, different crop, different scale) and still land as the
      // same visual size on screen. Must be the EFFECTIVE (post-safety-cap) scale, not the raw
      // cover-scale, or this silently drifts whenever the cap engages.
      var currentScale = typeof effectiveScale === 'number' ? effectiveScale : (typeof scale === 'number' ? scale : 1);

      var content = this._sceneContent(step);
      var usingSeatWidthLayout = !!(config && config.seat_width > 0);
      var slots = this._resolveSlots(step, config, artboard, content, currentScale);

      var desired = [];
      var mainIdx = 0;
      var compIdx = 0;
      for (var i = 0; i < slots.length; i++) {
        var slot = slots[i];
        var label = slot.label || '';
        var src = '';
        if (label.indexOf('main_') === 0) {
          var n = parseInt(label.slice(5), 10) || (mainIdx + 1);
          if (n > content.mains) continue;
          src = content.mainCutout;
          mainIdx++;
          // Hero scale (per-size Theme Editor setting) grows/shrinks the currently selected
          // size's cutout — anchor stays bottom-center, so it grows upward from the same seat
          // point rather than drifting. Skipped for seat_width-driven layouts: their item size
          // is already derived from the Size step's OWN already-scaled baseline pixel width
          // (see artDirectedSlots), so re-applying it here would double-scale.
          if (!usingSeatWidthLayout && content.heroScale && content.heroScale !== 1) {
            slot = { label: slot.label, x: slot.x, y: slot.y, anchor: slot.anchor, z_index: slot.z_index,
              rotation: slot.rotation, scale: slot.scale, opacity: slot.opacity,
              width: (slot.width || 0) * content.heroScale, height: (slot.height || 0) * content.heroScale };
          }
          // Capture the Size step's rendered baseline (in real on-screen px, already reflecting
          // heroScale) so every other step can reproduce the EXACT same visual size regardless
          // of that step's own artboard/photo configuration — this is the single source of
          // truth "how big is one Gloomee right now" flows from.
          if (step === 'size' && n === 1) {
            this._baselineItemPx = this._baselineItemPx || {};
            this._baselineItemPx[bp] = {
              width: (slot.width || 0) * currentScale,
              height: (slot.height || 0) * currentScale,
            };
          }
        } else if (label.indexOf('companion_') === 0) {
          var cn = (parseInt(label.slice(10), 10) || (compIdx + 1)) - 1;
          if (cn >= content.companions.length) continue;
          src = content.companions[cn].cutout || '';
          compIdx++;
        } else if (label === 'reward_1') {
          if (!content.reward) continue;
          src = content.reward.cutout || '';
        } else {
          continue;
        }
        if (!src) continue;
        desired.push({ slot: slot, src: src });
      }

      this._reconcileSceneItems(productsEl, desired, artboard);
      if (annotationsEl) this._renderAnnotations(step, config, artboard, annotationsEl, slots, content);
      if (usingSeatWidthLayout) {
        this._applyWorstCaseSpacing(bp, root.closest('.gax__pane'), step);
        // Every caller of _renderScene must end with the header's scale correctly (re)applied —
        // _applyWorstCaseSpacing above only resets this pane's own intro to unscaled, it never
        // decides the value. Belongs here, not left to callers to remember: _activatePane and
        // the background-image-load listener both call _renderScene for a single step directly,
        // never through _renderAllScenes, so if this lived only after that loop those paths
        // would leave the header permanently unscaled.
        this._applyGlobalIntroScale();
      }
    }

    /* The largest crew that can EVER appear (3 mains at the biggest configured size), rendered
       invisibly in the exact same coordinate frame as the real crew (see _updateGhostSlots /
       the ghost measurement in _renderScene) and reserved once per viewport — never recomputed
       from whatever is currently selected, so headers and cards never move or resize as the
       customer clicks around. There's no hand-derived trig here: the browser's own layout
       engine placed the ghost using the exact same _applySlotStyle percentage math the real
       crew uses, so measuring it with getBoundingClientRect() can't drift from reality, and it
       naturally re-derives correctly on every resize since it's measured fresh each render. */
    _maxHeroScale() {
      if (this._cachedMaxHero != null) return this._cachedMaxHero;
      var max = 1;
      (this.sizeCards || []).forEach(function (c) {
        var h = clamp((parseFloat(c.dataset.heroScale) || 100) / 100, 0.3, 1);
        if (h > max) max = h;
      });
      this._cachedMaxHero = max;
      return max;
    }

    // artDirectedSlots reserves every item's box at a fixed 1.18 height/width ratio so the
    // ghost's geometry stays simple and predictable, but the real cutout artwork inside that
    // box is often more square — object-fit: contain + object-position: bottom center then
    // leaves a real, invisible sliver at the TOP of the box (the bottom edge always lands on
    // the real artwork, so it never needs correcting). Left uncorrected, the ceiling
    // reservation treats that empty sliver as occupied and shrinks the header more than the
    // artwork actually requires. Preloads every size's cutout once — a handful of small
    // images, cached forever, never re-fetched per selection — and re-renders as each one
    // resolves so the credit lands as soon as it's known, without ever depending on which
    // size happens to be selected right now.
    _preloadCutoutAspects() {
      var self = this;
      this._cutoutAspects = [];
      var seen = {};
      (this.sizeCards || []).forEach(function (c) {
        var src = c.dataset.cutout;
        if (!src || seen[src]) return;
        seen[src] = true;
        var img = new Image();
        img.onload = function () {
          if (img.naturalWidth > 0 && img.naturalHeight > 0) {
            self._cutoutAspects.push(img.naturalHeight / img.naturalWidth);
            self._cachedCutoutAspect = null;
            self._renderAllScenes();
          }
        };
        img.src = src;
      });
    }

    // The safest (least generous) known real aspect ratio across every size's cutout — using
    // the tallest-relative-to-width one guarantees the credit never over-estimates how much of
    // the box any actual selectable size leaves empty. Capped at the box's own 1.18 so a
    // cutout taller than the box (height-constrained instead of width-constrained) never
    // produces a negative credit. Defaults to 1.18 (zero credit, today's original behavior)
    // until at least one image has actually confirmed it's safe to give room back.
    _cutoutSafeAspect() {
      if (this._cachedCutoutAspect != null) return this._cachedCutoutAspect;
      var BOX_ASPECT = 1.18;
      var max = 0;
      (this._cutoutAspects || []).forEach(function (a) { if (a > max) max = a; });
      this._cachedCutoutAspect = max > 0 ? Math.min(max, BOX_ASPECT) : BOX_ASPECT;
      return this._cachedCutoutAspect;
    }

    // Invisible (visibility:hidden) copies of the worst case the STEP can actually show, sized/
    // positioned with the SAME _applySlotStyle percentage math as the real crew, inside the SAME
    // artboardEl — so the ghost box IS exactly where the biggest possible real render would be,
    // on this exact viewport, right now. The Size step only ever shows ONE item (quantity is
    // chosen on the next step), so ghosting all 3 mains there would over-reserve card space for
    // a spread that can never happen — it ghosts just the single, tallest-reaching slot from the
    // 3-main arrangement (main_1: the rear-center one) instead, so every step's header ceiling
    // lines up at the same height while only the multi-item steps reserve the full spread width.
    _updateGhostSlots(artboardEl, config, artboard, singleTall) {
      var ghostEl = artboardEl.querySelector(':scope > [data-gax-scene-ghost]');
      if (!ghostEl) {
        ghostEl = document.createElement('div');
        ghostEl.className = 'gax-scene__ghost';
        ghostEl.setAttribute('data-gax-scene-ghost', '');
        ghostEl.setAttribute('aria-hidden', 'true');
        artboardEl.appendChild(ghostEl);
      }
      if (!config) {
        ghostEl.innerHTML = '';
        return ghostEl;
      }
      var allMains = artDirectedSlots(3, [], 0, artboard, config.seat_width, this._maxHeroScale(), config.spread || config.cluster_overlap)
        .filter(function (s) { return s.label.indexOf('main_') === 0; });
      var mains = singleTall ? allMains.filter(function (s) { return s.label === 'main_1'; }) : allMains;
      while (ghostEl.children.length < mains.length) {
        var d = document.createElement('div');
        d.className = 'gax-scene__ghost-item';
        ghostEl.appendChild(d);
      }
      while (ghostEl.children.length > mains.length) ghostEl.removeChild(ghostEl.lastChild);
      for (var i = 0; i < mains.length; i++) {
        this._applySlotStyle(ghostEl.children[i], mains[i], artboard);
      }
      return ghostEl;
    }

    // The header's shrink-to-fit scale has to be the SAME number on every pane, or the text
    // visibly grows/shrinks as the customer navigates between steps with different copy
    // lengths — even though the crew's own reserved ceiling is already identical everywhere.
    // Reads every step's natural (un-scaled) header height — including inactive panes, which
    // stay measurable since they're only visibility:hidden, never display:none — and takes
    // the tallest, so whichever step has the longest copy is what sets the shared scale for
    // all of them.
    _maxIntroNaturalHeight() {
      var self = this;
      var max = 0;
      this.stepOrder.forEach(function (step) {
        var introEl = self.querySelector('[data-gax-pane="' + step + '"] .gax__intro');
        if (!introEl) return;
        // .gax__intro is a grid item and stretches to fill its row's full height by default
        // (align-items: stretch, the grid default) — its own box tells us nothing about its
        // actual text size. scrollHeight doesn't help either: a stretched box isn't
        // overflowing, it's just bigger than its content with empty space below, so
        // scrollHeight reports the same inflated number as offsetHeight. align-self: start
        // un-stretches it for one measurement so offsetHeight reflects only what the text
        // itself actually needs, then restores.
        var prevAlignSelf = introEl.style.alignSelf;
        var prevMaxHeight = introEl.style.maxHeight;
        var prevOverflow = introEl.style.overflow;
        introEl.style.alignSelf = 'start';
        introEl.style.maxHeight = '';
        introEl.style.overflow = '';
        var h = introEl.offsetHeight;
        introEl.style.alignSelf = prevAlignSelf;
        introEl.style.maxHeight = prevMaxHeight;
        introEl.style.overflow = prevOverflow;
        if (h > max) max = h;
      });
      return max;
    }

    _applyWorstCaseSpacing(bp, pane, step) {
      if (!pane) return;
      var entry = this._worstCaseRects && this._worstCaseRects[step];
      if (!entry || !entry.rects.length) return;
      var paneRect = entry.paneRect;

      // The ghost's box top sits above where the real artwork visually starts (see
      // _cutoutSafeAspect) — credit that empty sliver back before it becomes the header's
      // ceiling. Only the top needs this: object-position: bottom center means the box's
      // bottom edge already lands exactly on the real artwork, so rightX/bottomY (card column
      // width, floor position) stay exactly as measured.
      var topEmptyFrac = Math.max(0, 1 - this._cutoutSafeAspect() / 1.18);
      var topY = Infinity;
      var rightX = -Infinity;
      var bottomY = -Infinity;
      entry.rects.forEach(function (r) {
        var top = r.top - paneRect.top + r.height * topEmptyFrac;
        var right = r.right - paneRect.left;
        var bottom = r.bottom - paneRect.top;
        if (top < topY) topY = top;
        if (right > rightX) rightX = right;
        if (bottom > bottomY) bottomY = bottom;
      });
      var MARGIN = 14;
      var RIGHT_MARGIN = 2;
      var ceilingY = Math.max(0, topY - MARGIN);
      var rightEdgeX = rightX + RIGHT_MARGIN;
      var floorY = bottomY + MARGIN;

      // Desktop: the option-card column starts right after the real crew, not at a fixed
      // 42/58 split. Cards get every remaining pixel — never artificially narrowed.
      var contentEl = pane.querySelector('.gax__content');
      var col1 = 0;
      if (contentEl) {
        if (bp !== 'mobile') {
          var contentLeft = contentEl.getBoundingClientRect().left - paneRect.left;
          col1 = rightEdgeX - contentLeft;
          contentEl.style.gridTemplateColumns = col1 > 100 ? col1 + 'px minmax(0, 1fr)' : '';
        } else {
          contentEl.style.gridTemplateColumns = '';
        }
      }

      // Heading renders at full natural size whenever it fits, on both breakpoints — the
      // crew's position is never adjusted for the header on either one now, so this is the
      // ONE place that ever needs to give: instead of clipping (cutting text off), it scales
      // the WHOLE header down proportionally (transform: scale, from the top corner it's
      // anchored at) so every line, the eyebrow pill, all of it shrinks together and still
      // reads as one clean, intentional block instead of truncated text.
      // The heading gets a little MORE width than the card column reservation (INTRO_EXTRA) —
      // it's just text sitting over the photo, nothing physically needs to stay clear of it out
      // there the way the cards need to stay clear of the crew, so it can use a bit of the
      // gutter before the cards start without crowding anything.
      var INTRO_EXTRA = 60;
      var introEl = pane.querySelector('.gax__intro');
      if (introEl) {
        introEl.style.width = (bp !== 'mobile' && col1 > 100) ? (col1 + INTRO_EXTRA) + 'px' : '';
        introEl.style.maxHeight = '';
        introEl.style.overflow = '';
        // Scale itself is NOT decided here — see _applyGlobalIntroScale, called once after
        // every step in the pass has reserved its own ceiling. Deciding it per-pane here used
        // this step's OWN room, which isn't the same number every step (the Size step ghosts
        // only one item; Quantity/Companions ghost three) — the header would visibly resize
        // while navigating. Reset to unscaled now; the global pass applies the real value.
        introEl.style.transform = '';
        introEl.style.transformOrigin = '';
      }

      this._pinLedgerBelowCrew(bp, pane, step);
    }

    // Decides the header's shrink scale exactly once per render pass, after every step's own
    // worst-case ghost has been (re)measured by the loop in _renderAllScenes — using the
    // TIGHTEST room found across all of them, not just the active pane's own. Applied
    // identically to every pane's .gax__intro. This is what keeps the header one fixed size
    // for the whole session at this viewport: picking a different size/quantity, adding a
    // companion, or navigating between steps never touches it, only a real resize (or a
    // cutout image finishing its first load) does, because those are the only things that can
    // change any of the inputs below.
    _applyGlobalIntroScale() {
      var self = this;
      var maxNaturalHeight = this._maxIntroNaturalHeight();
      var topEmptyFrac = Math.max(0, 1 - this._cutoutSafeAspect() / 1.18);
      var MARGIN = 14;
      var minRoom = Infinity;
      this.stepOrder.forEach(function (step) {
        var entry = self._worstCaseRects && self._worstCaseRects[step];
        if (!entry || !entry.rects.length) return;
        var pane = self._paneEl(step);
        var introEl = pane && pane.querySelector('.gax__intro');
        if (!introEl) return;
        var paneRect = entry.paneRect;
        var topY = Infinity;
        entry.rects.forEach(function (r) {
          var top = r.top - paneRect.top + r.height * topEmptyFrac;
          if (top < topY) topY = top;
        });
        var ceilingY = Math.max(0, topY - MARGIN);
        var introTop = introEl.getBoundingClientRect().top - paneRect.top;
        var room = ceilingY - introTop;
        if (room < minRoom) minRoom = room;
      });
      var scale = 1;
      if (minRoom !== Infinity && maxNaturalHeight > 0 && maxNaturalHeight > minRoom) {
        scale = clamp(minRoom / maxNaturalHeight, 0.6, 1);
      }
      this.stepOrder.forEach(function (step) {
        var pane = self._paneEl(step);
        var introEl = pane && pane.querySelector('.gax__intro');
        if (!introEl) return;
        if (scale < 1) {
          introEl.style.transformOrigin = 'top left';
          introEl.style.transform = 'scale(' + scale.toFixed(4) + ')';
        } else {
          introEl.style.transform = '';
          introEl.style.transformOrigin = '';
        }
      });
    }

    // Some of what determines the deck/receipt's real height and position (card media,
    // badges, the receipt's line items) finishes rendering AFTER _renderScene already measured
    // and positioned things — wait two animation frames (past the current layout/paint cycle)
    // and re-check once it's actually settled, reusing the crew's already-computed position
    // (that part doesn't change). On top of that, the floor target itself can carry its own
    // multi-hundred-ms entrance animation (the "rise" choreography) — the in-function
    // neutralization handles a still-running one correctly, but the FINAL applied margin/scale
    // can still visually fight the animation's own transform while it's active, so also
    // re-check once when it actually finishes, rather than trying to guess a timeout.
    _deferPinLedger(pane, step) {
      var self = this;
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          self._pinLedgerBelowCrew(self._sceneBreakpoint(), pane, step);
        });
      });
      var floorTarget = pane.querySelector('.gax__summary-panel') || pane.querySelector('.gax__deck');
      if (floorTarget && !floorTarget.dataset.gaxLedgerAnimHook) {
        floorTarget.dataset.gaxLedgerAnimHook = '1';
        floorTarget.addEventListener('animationend', function () {
          self._pinLedgerBelowCrew(self._sceneBreakpoint(), pane, step);
        });
      }
    }

    // The real pill (.gax-scene__pill) is white-space: nowrap — always exactly one line
    // regardless of text length — so its rendered height is stable and safe to read
    // directly rather than guess at. Its X/Y POSITION is still never read here (that part
    // stays ghost-derived, below) — only its height, which doesn't depend on where the
    // pill currently sits or what state it's mid-transition through. Falls back to a
    // reasonable default only on the very first render, before any pill has ever painted
    // in this pane yet.
    _pillHeight(pane) {
      var pillEl = pane && pane.querySelector('.gax-scene__pill');
      var h = pillEl && pillEl.getBoundingClientRect().height;
      return h > 0 ? h : 28;
    }

    // Mobile: the deck/ledger never starts above the crew's worst-case lowest point PLUS its
    // pill. The pill's own X/Y position is never read here — it's derived from the ghost (a
    // fixed distance below the worst-case crew's bottom, mirroring exactly how
    // _renderAnnotations places the real one) — so this stays stable regardless of what's
    // selected or where the real pill happens to be mid-transition right now; only its
    // HEIGHT (see _pillHeight) is read from the real element, since that part is stable
    // regardless of position. Split out from _applyWorstCaseSpacing so it can be re-run
    // standalone via _deferPinLedger above, once the deck/receipt's own real content (which
    // finishes rendering later) has settled.
    _pinLedgerBelowCrew(bp, pane, step) {
      if (!pane || bp !== 'mobile') return;
      var entry = this._worstCaseRects && this._worstCaseRects[step];
      if (!entry || !entry.rects.length) return;
      var paneRect = entry.paneRect;
      var bottomY = -Infinity;
      entry.rects.forEach(function (r) {
        var bottom = r.bottom - paneRect.top;
        if (bottom > bottomY) bottomY = bottom;
      });
      // A small, fixed gap either side of the pill's own real height (see _pillHeight) —
      // together these replace what used to be much larger round-number guesses that
      // reserved far more clearance than the pill actually needs, showing up as visible
      // empty space between the pill and the deck/receipt below it.
      var GAP_BEFORE_PILL = 6;
      var GAP_AFTER_PILL = 8;
      var floorY = bottomY + GAP_BEFORE_PILL + this._pillHeight(pane) + GAP_AFTER_PILL;

      var floorTarget = pane.querySelector('.gax__summary-panel') || pane.querySelector('.gax__deck');
      if (!floorTarget) return;
      // Cards/receipt render at their own natural, content-driven size — never stretched to
      // fill leftover space just because it's there, and every card in a row still matches
      // its siblings' height (align-items: stretch, in CSS — unchanged). They're only pulled
      // away from their natural (bottom-anchored via margin-top: auto) position for one of two
      // reasons: pushed down if that natural position would overlap the crew, or — if even the
      // natural size can't fit between the floor and the pane's own bottom — scaled down
      // (never scrolled) just enough to fit, uniformly, so it still reads as one clean block
      // and every card shrinks together rather than reflowing oddly.
      var isDeck = floorTarget.classList.contains('gax__deck');
      floorTarget.style.marginTop = '';
      floorTarget.style.minHeight = '';
      floorTarget.style.height = '';
      floorTarget.style.width = '';
      floorTarget.style.overflow = '';
      floorTarget.style.transform = '';
      floorTarget.style.transformOrigin = '';
      floorTarget.style.marginBottom = '';
      // This element used to carry its own entrance animation (the "rise" choreography,
      // gax-build-rise) with a CSS-driven transform (translateY) layered on top of
      // whatever this function sets — .gax__summary-panel's copy of that animation was
      // changed to the opacity-only gax-build-fade specifically so this function can own
      // `transform` outright (see the CSS comment by that keyframe). If it's still mid-flight, every
      // getBoundingClientRect() read below comes back offset by the animation's CURRENT
      // translateY, corrupting every push/compress decision that follows. Read that offset
      // directly off the computed transform matrix (its f component — already in real,
      // zoom-adjusted px, the same units getBoundingClientRect uses) and subtract it back
      // out of each reading, rather than toggling the animation property off and on to
      // "neutralize" it: that toggle is a known way to force a CSS animation to restart
      // from its first frame, and doing so from inside a function that itself gets re-run
      // by that same animation's own animationend event created a feedback loop — each
      // restart eventually re-fired animationend, re-running this, restarting it again —
      // visibly re-triggering the entrance animation and re-adjusting the receipt's size/
      // position for seconds after it should have settled once. A single synchronous call
      // never crosses a rendered frame, so the animation's progress (and this offset)
      // can't change mid-call — reading it once up front is valid for every measurement
      // below.
      var animM = getComputedStyle(floorTarget).transform.match(/^matrix\(([^)]+)\)$/);
      var animOffsetY = animM ? parseFloat(animM[1].split(',')[5]) || 0 : 0;
      var naturalTop = floorTarget.getBoundingClientRect().top - paneRect.top - animOffsetY;
      var naturalHeight = floorTarget.getBoundingClientRect().height;
      // margin-top: auto (the CSS default here) and an explicit pixel margin-top are NOT
      // the same coordinate system: auto consumes whatever free space the flex column has
      // above this item, flushing it to the bottom, while an explicit value — even 0 — is
      // measured from the plain, zero-margin flow position (right after the previous
      // sibling). naturalTop above is the AUTO position; below is the FLOW position, needed
      // as the correct baseline the moment this sets an EXPLICIT margin-top of its own —
      // using naturalTop there instead (as an earlier version of this did) silently adds
      // the fix's own delta on top of the auto-margin's delta too, landing far short of the
      // intended target.
      floorTarget.style.marginTop = '0px';
      var flowTop = floorTarget.getBoundingClientRect().top - paneRect.top - animOffsetY;
      floorTarget.style.marginTop = '';
      // The pane's own real bottom edge — the hard, physical boundary nothing may ever
      // cross (.gax__pane scrolls internally, overflow-y: auto, as a last resort, and that
      // scroll is exactly what "never" means here).
      var BOTTOM_MARGIN = 8;
      var bottomLimit = paneRect.height - BOTTOM_MARGIN;
      var maxAllowedHeight = Math.max(0, bottomLimit - floorY);
      if (isDeck) {
        // Cards fill the available [floorY, bottomLimit] window rather than sitting at
        // bare natural size with an empty gap above them — grow to close that gap when
        // there's slack, shrink when there isn't. Capped so a sparse deck on a very tall
        // phone or tablet doesn't blow up into absurdly large cards; floored (0.65) for
        // extreme compression — cards are the primary decision-making UI (price, size,
        // CTA), so some crew overlap in a genuinely extreme case is the better trade than
        // unreadable text.
        //
        // transform: scale(), not zoom: iOS Safari's support for the `zoom` property is
        // inconsistent (silently ignored on some versions), which used to leave this whole
        // fit-to-space system inert there — the deck rendered at natural size, could overflow
        // the pane, and every margin computed below (sized for a scale that never actually
        // applied) landed wildly wrong. transform is universally supported and, applied
        // uniformly via scale() (both axes), doesn't distort anything inside the way a Y-only
        // scaleY() would (most visibly the option thumbnails, stretched thinner than their
        // real aspect ratio). Unlike zoom, transform doesn't shrink the element's own layout
        // footprint — margin-bottom (below) fakes that so .gax__pane's overflow-y: auto still
        // sees the true, scaled height rather than the wider, pre-transform one. .gax__deck
        // itself carries no competing CSS transform/animation (only its .gax__card children
        // animate their own), so it's safe for this function to own `transform` here outright.
        var GROW_CAP_DECK = 1.4;
        var deckNaturalWidth = floorTarget.getBoundingClientRect().width;
        var deckScale = naturalHeight > 0 ? clamp(maxAllowedHeight / naturalHeight, 0.65, GROW_CAP_DECK) : 0.65;
        // The compensated width set below is WIDER than natural whenever deckScale < 1 (it
        // has to be — the scale transform will shrink it back down by that same factor, see
        // the comment on the width line itself). But that width lands on the real,
        // pre-transform box, so the cards inside see the WIDER box for their own text
        // wrapping — sometimes needing measurably less height than naturalHeight (measured
        // at the narrower, un-compensated width, above) predicted. And that shorter height
        // feeds back into a bigger scale, which feeds back into a narrower compensated
        // width, which feeds back into a taller height again — width, height and scale are
        // circular here, not a one-shot correction, so this settles it with a few rounds of
        // "measure height at the current guess's width, refine the guess" rather than the
        // single pass that used to systematically over- or under-shoot (leaving the deck
        // short of the pane's bottom edge, or past it).
        // Text wrapping flips discretely (a line either wraps or it doesn't) rather than
        // smoothly with width, so feeding each pass's raw result straight back in can bounce
        // forever between the "wrapped" and "unwrapped" scale instead of settling —
        // averaging each new guess with the previous one damps that oscillation out.
        var deckWidthAdjustedHeight = naturalHeight;
        for (var deckFitPass = 0; deckFitPass < 8; deckFitPass++) {
          floorTarget.style.width = (deckNaturalWidth / deckScale) + 'px';
          deckWidthAdjustedHeight = floorTarget.getBoundingClientRect().height;
          if (deckWidthAdjustedHeight <= 0) break;
          var deckRawScale = clamp(maxAllowedHeight / deckWidthAdjustedHeight, 0.65, GROW_CAP_DECK);
          deckScale = deckFitPass === 0 ? deckRawScale : (deckScale + deckRawScale) / 2;
        }
        // One last measurement at the settled scale so deckWidthAdjustedHeight/deckScale are
        // a consistent pair for the overflow check and anchoring math below — the loop's
        // final scale is a damped average, not necessarily an exact match for the last
        // height it measured.
        floorTarget.style.width = (deckNaturalWidth / deckScale) + 'px';
        deckWidthAdjustedHeight = floorTarget.getBoundingClientRect().height || deckWidthAdjustedHeight;
        // transform-origin: top left anchors the visual top-left corner to the untransformed
        // flow position — unlike zoom, an explicit margin-top here is a plain, unscaled pixel
        // offset, not something the transform itself rescales.
        floorTarget.style.transformOrigin = 'top left';
        floorTarget.style.transform = 'scale(' + deckScale + ')';
        // Width is set once more here because transform doesn't change the box's own inline
        // width value — the compensation still has to match whatever scale actually ships.
        floorTarget.style.width = (deckNaturalWidth / deckScale) + 'px';
        // Fakes zoom's box-shrinking side effect (see the comment above): without this, the
        // element's un-transformed layout height (deckWidthAdjustedHeight, always taller than
        // its scaled visual height whenever deckScale < 1) is what .gax__pane's overflow-y:
        // auto and any following sibling's flow position would see instead.
        floorTarget.style.marginBottom = (deckWidthAdjustedHeight * (deckScale - 1)) + 'px';
        floorTarget.style.marginTop = '0px';
        var deckFlowTop = floorTarget.getBoundingClientRect().top - paneRect.top - animOffsetY;
        if (deckWidthAdjustedHeight * deckScale > maxAllowedHeight) {
          // Even at the 0.65 floor the scaled box is still taller than the window —
          // bottom-anchor is the only way to guarantee it never crosses the pane's real
          // bottom edge.
          floorTarget.style.marginTop = (bottomLimit - deckWidthAdjustedHeight * deckScale - deckFlowTop) + 'px';
        } else {
          // Fits (possibly with leftover slack if grow-capped): top-anchor to the crew+
          // pill reservation so any leftover slack shows up at the bottom (next to the
          // dock) instead of reappearing as a gap next to the pill.
          floorTarget.style.marginTop = (floorY - deckFlowTop) + 'px';
        }
      } else {
        // Receipt: always fills the available [floorY, bottomLimit] window rather than
        // sitting at its bare natural size with an empty gap above it — grows to close
        // that gap when there's slack, shrinks when there isn't, via transform: scale(),
        // same mechanism and reasoning as the deck branch above (see its comment: iOS
        // Safari's inconsistent `zoom` support used to leave this whole system inert
        // there). This element carries its own entrance animation directly
        // (gax-build-fade, opacity-only — it used to be gax-build-rise, which also set
        // `transform`; a fill-mode: both animation's "to" value permanently wins the
        // cascade over an inline transform, even long after the animation has finished
        // playing, which is exactly why it was changed, freeing `transform` for this
        // function to own here). Capped so a sparse receipt on a very tall phone doesn't
        // blow up into oversized text; floored (0.65, same floor the deck branch uses) so
        // extreme compression never shrinks it past legible — a 0.05 floor let the receipt
        // shrink to 5% of its natural size under genuine space pressure, reading as "not
        // showing" even though something was technically still rendered. .gax__pane's
        // overflow-y: auto is still there as the last-resort fallback past this floor.
        var GROW_CAP = 1.4;
        var receiptScale = maxAllowedHeight > 0 ? clamp(maxAllowedHeight / naturalHeight, 0.65, GROW_CAP) : 0.65;
        var naturalWidth = floorTarget.getBoundingClientRect().width;
        // transform-origin: top left, same anchoring the deck branch uses — the visual
        // top-left corner stays pinned to the untransformed flow position.
        floorTarget.style.transformOrigin = 'top left';
        floorTarget.style.transform = 'scale(' + receiptScale + ')';
        // transform rescales its OWN rendering — width included, same as zoom used to — so
        // left untouched a grow (scale > 1) would overflow past the pane's sides
        // horizontally. Setting width to natural/scale cancels that multiplication out,
        // leaving the rendered width exactly where it started while height genuinely
        // changes.
        floorTarget.style.width = (naturalWidth / receiptScale) + 'px';
        // Fakes zoom's box-shrinking side effect (see the deck branch's identical
        // comment): without this, the element's un-transformed layout height (naturalHeight,
        // always taller than its scaled visual height whenever receiptScale < 1) is what
        // .gax__pane's overflow-y: auto would see instead, defeating the whole point of
        // scaling it down to fit.
        floorTarget.style.marginBottom = (naturalHeight * (receiptScale - 1)) + 'px';
        // Bottom-anchor to the pane's real bottom, same target the deck's compress branch
        // uses.
        floorTarget.style.marginTop = '0px';
        var receiptFlowTop = floorTarget.getBoundingClientRect().top - paneRect.top - animOffsetY;
        floorTarget.style.marginTop = (bottomLimit - naturalHeight * receiptScale - receiptFlowTop) + 'px';
      }
    }

    /* Keyed update instead of an innerHTML rebuild: an item that persists across a state
       change keeps its element, so the CSS transition tweens it from its old slot to its new
       slot (translate/resize) — a plush "walks" to its new position when another is added.
       New items fade/settle in; removed items fade out before leaving the DOM. */
    _reconcileSceneItems(productsEl, desired, artboard) {
      var self = this;
      var byKey = {};
      Array.prototype.slice.call(productsEl.children).forEach(function (el) {
        if (el.dataset.slotKey) byKey[el.dataset.slotKey] = el;
      });

      var keep = {};
      var fresh = [];
      desired.forEach(function (d) {
        var key = d.slot.label || '';
        keep[key] = true;
        var el = byKey[key];
        if (!el) {
          el = document.createElement('img');
          el.className = 'gax-scene__item';
          el.dataset.slotKey = key;
          el.alt = '';
          el.loading = 'lazy';
          productsEl.appendChild(el);
          fresh.push(el);
        } else if (el.dataset.leaving) {
          delete el.dataset.leaving;
          el.classList.remove('is-out');
        }
        if (el.getAttribute('src') !== d.src) el.setAttribute('src', d.src);
        self._applySlotStyle(el, d.slot, artboard);
      });

      Array.prototype.slice.call(productsEl.children).forEach(function (el) {
        var key = el.dataset.slotKey;
        if (keep[key] || el.dataset.leaving) return;
        el.dataset.leaving = 'true';
        el.classList.add('is-out');
        window.setTimeout(function () {
          if (el.dataset.leaving) el.remove();
        }, self.isReducedMotion ? 20 : 380);
      });

      if (fresh.length) {
        window.requestAnimationFrame(function () {
          fresh.forEach(function (el, i) {
            window.setTimeout(function () { el.classList.add('is-in'); }, i * 70);
          });
        });
      }
    }

    _applySlotStyle(el, slot, artboard) {
      var nx = (slot.x || 0) / artboard.width * 100;
      var ny = (slot.y || 0) / artboard.height * 100;
      var nw = (slot.width || artboard.width * 0.3) / artboard.width * 100;
      var nh = (slot.height || artboard.height * 0.4) / artboard.height * 100;
      var anchor = ANCHORS[slot.anchor || 'bottom-center'] || ANCHORS['bottom-center'];
      var transform = 'translate(' + -(anchor[0] * 100) + '%, ' + -(anchor[1] * 100) + '%)';
      if (slot.rotation) transform += ' rotate(' + slot.rotation + 'deg)';
      if (slot.scale && slot.scale !== 1) transform += ' scale(' + slot.scale + ')';
      el.style.left = nx.toFixed(3) + '%';
      el.style.top = ny.toFixed(3) + '%';
      el.style.width = nw.toFixed(3) + '%';
      el.style.height = nh.toFixed(3) + '%';
      el.style.transform = transform;
      el.style.zIndex = String(slot.z_index || 2);
      if (slot.opacity != null) {
        el.style.setProperty('--slot-opacity', String(slot.opacity));
      } else {
        el.style.removeProperty('--slot-opacity');
      }
    }

    _slotBoxOf(slot) {
      var anchor = ANCHORS[slot.anchor || 'bottom-center'] || ANCHORS['bottom-center'];
      var w = slot.width || 0, h = slot.height || 0;
      var left = (slot.x || 0) - anchor[0] * w;
      var top = (slot.y || 0) - anchor[1] * h;
      return { left: left, top: top, right: left + w, bottom: top + h, width: w, height: h };
    }

    _resolveSlotBox(slots, label, content) {
      // "group" is a virtual label: the union bounding box of every item actually rendering
      // right now (however many mains/companions that is) — so an annotation can point at
      // "the group" generically instead of one specific item's corner, regardless of count.
      if (label === 'group') {
        var active = (slots || []).filter(function (s) {
          var lbl = s.label || '';
          if (lbl.indexOf('main_') === 0) {
            var n = parseInt(lbl.slice(5), 10) || 0;
            return content && n <= content.mains;
          }
          if (lbl.indexOf('companion_') === 0) {
            var cn = parseInt(lbl.slice(10), 10) || 0;
            return content && cn <= content.companions.length;
          }
          if (lbl === 'reward_1') return !!(content && content.reward);
          return false;
        });
        if (!active.length) return null;
        var self = this;
        var boxes = active.map(function (s) { return self._slotBoxOf(s); });
        var left = Math.min.apply(null, boxes.map(function (b) { return b.left; }));
        var top = Math.min.apply(null, boxes.map(function (b) { return b.top; }));
        var right = Math.max.apply(null, boxes.map(function (b) { return b.right; }));
        var bottom = Math.max.apply(null, boxes.map(function (b) { return b.bottom; }));
        return { left: left, top: top, right: right, bottom: bottom, width: right - left, height: bottom - top };
      }
      var slot = (slots || []).filter(function (s) { return s.label === label; })[0];
      if (!slot) return null;
      return this._slotBoxOf(slot);
    }

    _pointOnBox(box, anchorName, marginX, marginY) {
      var a = ANCHORS[anchorName || 'top-center'] || ANCHORS['top-center'];
      var x = box.left + a[0] * box.width;
      var y = box.top + a[1] * box.height;
      // push outward from the box's own center, along the direction implied by the anchor —
      // e.g. a "top-right" anchor pushes up and to the right, "bottom-center" pushes straight down.
      var dirX = (a[0] - 0.5) * 2;
      var dirY = (a[1] - 0.5) * 2;
      x += dirX * (marginX || 0);
      y += dirY * (marginY || 0);
      return { x: x, y: y };
    }

    /* Scene label: a single pill, horizontally centred on whatever crew is standing there
       and parked just below the seat. This replaced the handwritten caption + curved arrow,
       which needed collision maths against the heading, the cards and the crew itself and
       could still drift. A pill below the seat has no such failure modes — there is nothing
       underneath the crew to collide with, at any size, count or breakpoint. */
    _renderAnnotations(step, config, artboard, annotationsEl, slots, content) {
      var candidates = this._sceneStateCandidates(step);
      var list = config && Array.isArray(config.annotations) ? config.annotations : null;

      var text = '';
      if (list && list.length) {
        for (var i = 0; i < list.length; i++) {
          var a = list[i];
          var states = toList(a.show_on_states);
          if (states.length && !states.some(function (st) { return candidates.indexOf(st) > -1; })) continue;
          text = a.text || '';
          break;
        }
      }
      if (!text) text = annotationsEl.dataset.defaultAnnotation || '';

      if (!text) {
        annotationsEl.innerHTML = '';
        return;
      }

      // Centre on the crew when there is one; otherwise fall back to the artboard centre.
      var groupBox = this._resolveSlotBox(slots, 'group', content);
      var cx = groupBox ? (groupBox.left + groupBox.width / 2) : (artboard.width * 0.5);
      var cy = groupBox ? groupBox.bottom : (artboard.height * 0.94);
      var nx = clamp(cx / artboard.width * 100, 8, 92);
      var ny = (cy + artboard.height * 0.03) / artboard.height * 100;

      // Keep the pill on the pane. The crew can sit low enough that 'just below the seat'
      // would fall off the bottom edge, so clamp in real screen space and, if there is no
      // room underneath, tuck it just above the seat line instead.
      var aRect = annotationsEl.getBoundingClientRect();
      var annotationsPane = annotationsEl.closest('.gax__pane');
      var pRect = annotationsPane.getBoundingClientRect();
      if (aRect.height > 0) {
        // Same real, measured pill height _pinLedgerBelowCrew uses (see _pillHeight) — this
        // used a hardcoded guess before, which pushed the pill's own clamped position
        // higher than it needed to be and left visible empty space between it and the
        // deck/receipt below, even after that floor was tightened to match.
        var PILL_H = this._pillHeight(annotationsPane);
        var screenY = aRect.top + (ny / 100) * aRect.height;
        var maxY = pRect.bottom - PILL_H - 14;
        // On mobile the option cards (or the Adopt receipt) overlay the lower part of the
        // photo — the pill must clear THAT edge too, not just the pane's own bottom, or it
        // ends up half-hidden behind the cards. Uses the SAME stable, ghost-based floor
        // _pinLedgerBelowCrew reserves for the deck/receipt — never the real element's own
        // current top — because the deck/receipt is guaranteed to never start above that
        // line, so the pill can rely on it directly instead of on the real element's
        // height, which fluctuates with content (a companion added, a longer receipt) and
        // would otherwise drag the pill's position around with it on every selection
        // change. Desktop's cards/receipt live in their own side column, never under the
        // pill, so this only matters on mobile.
        if (this._sceneBreakpoint() === 'mobile') {
          var coverEntry = this._worstCaseRects && this._worstCaseRects[step];
          if (coverEntry && coverEntry.rects.length) {
            var coverPaneRect = coverEntry.paneRect;
            var coverBottomY = -Infinity;
            coverEntry.rects.forEach(function (r) {
              var b = r.bottom - coverPaneRect.top;
              if (b > coverBottomY) coverBottomY = b;
            });
            // GAP_BEFORE_PILL + pill height + GAP_AFTER_PILL — matches
            // _pinLedgerBelowCrew's floorY exactly (PILL_H above is the same real,
            // measured height).
            var coverFloorY = coverBottomY + 6 + PILL_H + 8;
            maxY = Math.min(maxY, pRect.top + coverFloorY - PILL_H - 10);
          }
        }
        if (screenY > maxY) {
          screenY = maxY;
          ny = ((screenY - aRect.top) / aRect.height) * 100;
        }
        // The crew's own resting point can also land close enough to the header (its size is
        // never shrunk to make room, only nudged) that 'just below the seat' overlaps the
        // header text instead — clamp the pill's top edge to the header's real bottom too.
        // Measured off .gax__intro's LAST CHILD, not the container itself: .gax__intro is a
        // grid item that stretches to fill its row (align-items: stretch, the grid default),
        // so its own box reaches nearly to the bottom of a tall pane regardless of how short
        // the actual text is — using its bottom edge here would treat that empty stretched
        // space as "header," shoving the pill down toward the pane floor on tall viewports.
        // The last text child is never stretched, only sized to its own content.
        var introEl = this.querySelector('[data-gax-pane="' + step + '"] .gax__intro');
        var introLastChild = introEl && introEl.lastElementChild;
        if (introLastChild) {
          var introRect = introLastChild.getBoundingClientRect();
          if (introRect.height > 0) {
            var minY = introRect.bottom - pRect.top + 10;
            if (screenY - pRect.top < minY) {
              screenY = pRect.top + minY;
              ny = ((screenY - aRect.top) / aRect.height) * 100;
            }
          }
        }
      }

      annotationsEl.innerHTML =
        '<span class="gax-scene__pill" style="left:' + nx.toFixed(2) + '%;top:' + ny.toFixed(2) + '%;">' +
        escapeHtml(text) + '</span>';

      annotationsEl.classList.remove('is-settled');
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () { annotationsEl.classList.add('is-settled'); });
      });
    }
    _buildArrowPath(x1, y1, x2, y2, bounds) {
      if (bounds) {
        var cl = function (v, lo, hi) { return Math.max(lo, Math.min(hi, v)); };
        x1 = cl(x1, 4, bounds.w - 4); x2 = cl(x2, 4, bounds.w - 4);
        y1 = cl(y1, 4, bounds.h - 4); y2 = cl(y2, 4, bounds.h - 4);
      }
      // A gentle hand-drawn-feeling curve from (x1,y1) to (x2,y2), both in raw artboard-unit
      // coordinates — same space as slot x/y, so it always lines up exactly regardless of
      // viewport, and works in any direction (left, right, up, down, diagonal).
      var dx0 = x2 - x1, dy0 = y2 - y1;
      var dist0 = Math.sqrt(dx0 * dx0 + dy0 * dy0) || 1;
      // Small extra breathing room past the caller-provided start point.
      var lead = Math.min(dist0 * 0.06, 16);
      x1 += (dx0 / dist0) * lead;
      y1 += (dy0 / dist0) * lead;
      var dx = x2 - x1, dy = y2 - y1;
      var dist = Math.sqrt(dx * dx + dy * dy) || 1;
      var perpX = -dy / dist, perpY = dx / dist;
      var bend = Math.min(dist * 0.12, Math.abs(x2 - x1) * 0.5 + 18);
      var mx = (x1 + x2) / 2 + perpX * bend;
      var my = (y1 + y2) / 2 + perpY * bend;
      // Stop a little short of the exact target so the arrowhead doesn't bury itself in it.
      var shrink = Math.min(dist * 0.14, 40);
      var ex = x2 - (dx / dist) * shrink;
      var ey = y2 - (dy / dist) * shrink;
      var tx = ex - mx, ty = ey - my;
      var tlen = Math.sqrt(tx * tx + ty * ty) || 1;
      tx /= tlen; ty /= tlen;
      // Floor on the arrowhead so short arrows stay clearly readable, cap so long ones
      // don't turn into spears.
      var headLen = clamp(dist * 0.14, 15, 30);
      var headWide = headLen * 0.62;
      var nxp = -ty, nyp = tx;
      var hx1 = ex - tx * headLen + nxp * headWide;
      var hy1 = ey - ty * headLen + nyp * headWide;
      var hx2 = ex - tx * headLen - nxp * headWide;
      var hy2 = ey - ty * headLen - nyp * headWide;
      return '<path d="M ' + x1.toFixed(1) + ' ' + y1.toFixed(1) + ' Q ' + mx.toFixed(1) + ' ' + my.toFixed(1) + ' ' + ex.toFixed(1) + ' ' + ey.toFixed(1) + '" fill="none" stroke="currentColor" stroke-width="4" stroke-linecap="round" vector-effect="non-scaling-stroke"/>' +
        '<path d="M ' + ex.toFixed(1) + ' ' + ey.toFixed(1) + ' L ' + hx1.toFixed(1) + ' ' + hy1.toFixed(1) + ' M ' + ex.toFixed(1) + ' ' + ey.toFixed(1) + ' L ' + hx2.toFixed(1) + ' ' + hy2.toFixed(1) + '" fill="none" stroke="currentColor" stroke-width="4" stroke-linecap="round" vector-effect="non-scaling-stroke"/>';
    }

    /* ---------------- size selection video (plays over the scene) ---------------- */

    _maybePlaySizeVideo(cardEl) {
      var video = this.querySelector('[data-gax-hero-video]');
      var replayBtn = this.querySelector('[data-gax-replay]');
      if (!video) return;

      var isMobile = MOBILE_MQ.matches;
      var src = isMobile ? cardEl.dataset.videoMobile : cardEl.dataset.videoDesktop;
      var poster = cardEl.dataset.videoPoster;
      var shouldPlay = cardEl.dataset.playVideoOnSelect === 'true' && !this.isReducedMotion;
      var showReplay = cardEl.dataset.showReplay === 'true';

      video.pause();
      video.removeAttribute('src');
      video.hidden = true;
      if (replayBtn) replayBtn.hidden = true;
      if (!src || !shouldPlay) return;

      if (poster) video.setAttribute('poster', poster);
      video.src = src;
      video.hidden = false;

      var self = this;
      video.addEventListener('ended', function () {
        self._dispatchAnalytics('gloomee_media_complete', { sizeKey: cardEl.dataset.sizeKey });
        video.hidden = true;
        if (showReplay && replayBtn) replayBtn.hidden = false;
      }, { once: true });

      var playPromise = video.play();
      if (playPromise && playPromise.then) {
        playPromise
          .then(function () {
            self._dispatchAnalytics('gloomee_media_play', { sizeKey: cardEl.dataset.sizeKey });
          })
          .catch(function () {
            video.hidden = true;
          });
      }
    }

    _replaySizeVideo() {
      var video = this.querySelector('[data-gax-hero-video]');
      var replayBtn = this.querySelector('[data-gax-replay]');
      if (!video || !video.getAttribute('src')) return;
      video.hidden = false;
      video.currentTime = 0;
      video.play().catch(function () {});
      if (replayBtn) replayBtn.hidden = true;
      this._dispatchAnalytics('gloomee_media_replay', { sizeKey: this.state.selectedSizeKey });
    }

    /* ---------------- central pricing engine (integer cents throughout) ---------------- */

    _activeQuantityUnitPricing(saleUnit, originalUnit) {
      var d = this.state.quantityDiscount;
      if (this.discountStackModel === 'shopify_actual_only' || !d || !d.enabled || d.type === 'none') {
        return { effectiveUnit: saleUnit, quantitySavingsUnit: 0 };
      }

      var qty = this.state.selectedQuantity || 1;
      var effectiveUnit = saleUnit;
      var quantitySavingsUnit = 0;

      if (this.discountStackModel === 'quantity_overrides_size_sale') {
        if (d.type === 'percent') {
          effectiveUnit = Math.round(originalUnit * (1 - d.value / 100));
        } else if (d.type === 'amount_per_unit') {
          effectiveUnit = Math.max(0, originalUnit - Math.round(d.value * 100));
        } else if (d.type === 'fixed_bundle_price') {
          effectiveUnit = Math.max(0, Math.round((d.value * 100) / qty));
        }
        quantitySavingsUnit = originalUnit - effectiveUnit;
        return { effectiveUnit: effectiveUnit, quantitySavingsUnit: quantitySavingsUnit };
      }

      if (this.discountStackModel === 'additive_percent' && d.type === 'percent') {
        var sizePercent = originalUnit > 0 ? ((originalUnit - saleUnit) / originalUnit) * 100 : 0;
        var combinedPercent = Math.min(95, sizePercent + d.value);
        effectiveUnit = Math.round(originalUnit * (1 - combinedPercent / 100));
        quantitySavingsUnit = saleUnit - effectiveUnit;
        return { effectiveUnit: effectiveUnit, quantitySavingsUnit: quantitySavingsUnit };
      }

      if (d.type === 'percent') {
        quantitySavingsUnit = Math.round(saleUnit * d.value / 100);
        effectiveUnit = Math.max(0, saleUnit - quantitySavingsUnit);
      } else if (d.type === 'amount_per_unit') {
        quantitySavingsUnit = Math.round(d.value * 100);
        effectiveUnit = Math.max(0, saleUnit - quantitySavingsUnit);
        quantitySavingsUnit = saleUnit - effectiveUnit;
      } else if (d.type === 'fixed_bundle_price') {
        var bundleUnit = Math.max(0, Math.round((d.value * 100) / qty));
        effectiveUnit = Math.min(bundleUnit, saleUnit);
        quantitySavingsUnit = saleUnit - effectiveUnit;
      }
      return { effectiveUnit: effectiveUnit, quantitySavingsUnit: quantitySavingsUnit };
    }

    _calculatePricing() {
      var s = this.state;
      var lines = [];
      var originalMerchandiseValue = 0;
      var productSaleSavings = 0;
      var quantityDiscountSavings = 0;
      var otherDiscountSavings = 0;
      var shopifyActualOnly = this.discountStackModel === 'shopify_actual_only';

      if (s.selectedSizeKey && s.selectedSizeMerchandise) {
        var m = s.selectedSizeMerchandise;
        var qty = s.selectedQuantity || 1;
        var originalUnit = shopifyActualOnly ? m.price : (m.originalPrice || m.price);
        var saleUnit = m.price;
        var qtyResult = this._activeQuantityUnitPricing(saleUnit, originalUnit);
        var effectiveUnit = qtyResult.effectiveUnit;
        var productSaveUnit = originalUnit - saleUnit;
        var labels = [];
        if (!shopifyActualOnly && m.saleActive) labels.push('Size sale');
        if (!shopifyActualOnly && s.quantityDiscount && s.quantityDiscount.enabled && qtyResult.quantitySavingsUnit > 0) {
          labels.push(s.quantityDiscount.label || 'Quantity discount');
        }

        lines.push({
          key: 'size',
          title: m.title,
          image: m.cutout,
          badgeText: m.recommended ? m.badgeText : '',
          quantity: qty,
          originalUnitPrice: originalUnit,
          productSaleUnitPrice: saleUnit,
          effectiveUnitPrice: effectiveUnit,
          originalLineTotal: originalUnit * qty,
          effectiveLineTotal: effectiveUnit * qty,
          productSaleSavings: productSaveUnit * qty,
          quantitySavings: qtyResult.quantitySavingsUnit * qty,
          totalLineSavings: (productSaveUnit + qtyResult.quantitySavingsUnit) * qty,
          appliedDiscountLabels: labels,
        });

        originalMerchandiseValue += originalUnit * qty;
        productSaleSavings += productSaveUnit * qty;
        quantityDiscountSavings += qtyResult.quantitySavingsUnit * qty;
      }

      Object.keys(s.selectedUpsells).forEach(function (key) {
        var u = s.selectedUpsells[key];
        var originalUnit = shopifyActualOnly ? u.price : (u.originalPrice || u.price);
        var saveUnit = originalUnit - u.price;
        lines.push({
          key: key,
          title: u.title,
          image: u.cutout,
          badgeText: '',
          quantity: 1,
          originalUnitPrice: originalUnit,
          productSaleUnitPrice: u.price,
          effectiveUnitPrice: u.price,
          originalLineTotal: originalUnit,
          effectiveLineTotal: u.price,
          productSaleSavings: saveUnit,
          quantitySavings: 0,
          totalLineSavings: saveUnit,
          appliedDiscountLabels: !shopifyActualOnly && u.saleActive ? ['Companion sale'] : [],
        });
        originalMerchandiseValue += originalUnit;
        productSaleSavings += saveUnit;
      });

      // Gift line: the merchant's reward discount (100% = fully free) is display pricing —
      // it must be backed by a matching Shopify automatic discount, exactly like the manual
      // size/companion sale modes. The full value still counts toward original merchandise
      // value so the customer can see what the gift is worth.
      var giftSavings = 0;
      if (s.rewardSelection && s.rewardSelection.variantId) {
        var rFull = s.rewardSelection.price;
        var rPct = clamp(s.rewardSelection.discountPercent || 0, 0, 100);
        var rEffective = shopifyActualOnly ? rFull : Math.max(0, Math.round(rFull * (1 - rPct / 100)));
        var rSave = rFull - rEffective;
        lines.push({
          key: 'reward',
          title: s.rewardSelection.title,
          image: s.rewardSelection.cutout || '',
          badgeText: '',
          giftBadge: rSave > 0 ? (s.rewardSelection.badgeText || (rEffective === 0 ? 'Free gift' : 'Gift')) : '',
          isGift: true,
          quantity: 1,
          originalUnitPrice: rFull,
          productSaleUnitPrice: rEffective,
          effectiveUnitPrice: rEffective,
          originalLineTotal: rFull,
          effectiveLineTotal: rEffective,
          productSaleSavings: rSave,
          quantitySavings: 0,
          totalLineSavings: rSave,
          appliedDiscountLabels: rSave > 0 ? ['Gift'] : [],
        });
        originalMerchandiseValue += rFull;
        giftSavings += rSave;
      }

      var totalSavings = productSaleSavings + quantityDiscountSavings + otherDiscountSavings + giftSavings;
      var effectiveSubtotal = originalMerchandiseValue - totalSavings;
      var itemCount = lines.reduce(function (sum, l) { return sum + l.quantity; }, 0);

      // Shipping is a separate, display-only saving: it never touches the merchandise
      // subtotal (which has to keep matching the real cart), but it does count toward the
      // "you saved" figure the customer sees.
      var shippingSavings = s.freeShipping ? (this.shippingSavingsCents || 0) : 0;

      return {
        lines: lines,
        originalMerchandiseValue: originalMerchandiseValue,
        productSaleSavings: productSaleSavings,
        quantityDiscountSavings: quantityDiscountSavings,
        otherDiscountSavings: otherDiscountSavings,
        giftSavings: giftSavings,
        shippingSavings: shippingSavings,
        totalSavings: totalSavings,
        totalSavingsWithShipping: totalSavings + shippingSavings,
        effectiveSubtotal: effectiveSubtotal,
        itemCount: itemCount,
        freeShippingActive: s.freeShipping,
      };
    }

    _updateQuantityCardPricing() {
      var s = this.state;
      if (!s.selectedSizeMerchandise) return;
      var self = this;
      var originalUnit = s.selectedSizeMerchandise.originalPrice || s.selectedSizeMerchandise.price;
      var saleUnit = s.selectedSizeMerchandise.price;
      var title = s.selectedSizeMerchandise.title;
      var shipVisibleBySize = !s.selectedSizeMerchandise.isLarge;

      this.quantityCards.forEach(function (card) {
        var qty = parseInt(card.dataset.quantityValue, 10) || 1;
        var wrap = card.querySelector('[data-gax-qty-pricing]');
        var subtotalEl = card.querySelector('[data-gax-qty-subtotal]');
        var priceEl = card.querySelector('[data-gax-qty-price]');
        var eachEl = card.querySelector('[data-gax-qty-each]');
        var includesEl = card.querySelector('[data-gax-includes]');
        var shipEl = card.querySelector('[data-gax-qty-shipping]');
        if (!wrap) return;

        var discount = null;
        if (card.dataset.discountEnabled === 'true') {
          discount = {
            enabled: true,
            type: card.dataset.discountType || 'none',
            value: parseFloat(card.dataset.discountValue) || 0,
          };
        }
        var savedDiscount = self.state.quantityDiscount;
        var savedQty = self.state.selectedQuantity;
        self.state.quantityDiscount = discount;
        self.state.selectedQuantity = qty;
        var result = self._activeQuantityUnitPricing(saleUnit, originalUnit);
        self.state.quantityDiscount = savedDiscount;
        self.state.selectedQuantity = savedQty;

        var lineOriginal = originalUnit * qty;
        var lineEffective = result.effectiveUnit * qty;

        wrap.hidden = false;
        if (subtotalEl) {
          subtotalEl.hidden = !self.showOriginalPrices || lineEffective >= lineOriginal;
          subtotalEl.textContent = self._formatMoney(lineOriginal);
        }
        if (priceEl) priceEl.textContent = self._formatMoney(lineEffective);
        if (eachEl) eachEl.textContent = self.showQuantityEachPrice && qty > 1 ? self._formatMoney(result.effectiveUnit) + ' each' : '';
        if (includesEl) {
          includesEl.hidden = !self.showQuantityIncludesLine;
          includesEl.textContent = 'Includes: ' + qty + ' × ' + title;
        }
        // A quantity tier marked to force free shipping shows the badge (and, once
        // selected, actually grants it — see _recomputeDerived) regardless of the
        // size-based rule, e.g. Large normally doesn't ship free but a merchant can mark
        // the "2" tier to include it anyway.
        if (shipEl) shipEl.hidden = !(shipVisibleBySize || card.dataset.forceFreeShipping === 'true');

        // Quantity card figure: the selected size's cutout, repeated per quantity.
        var media = card.querySelector('[data-gax-qty-media]');
        if (media && s.selectedSizeMerchandise.cutout) {
          var imgs = '';
          for (var i = 0; i < Math.min(qty, 3); i++) {
            imgs += '<img src="' + escapeHtml(s.selectedSizeMerchandise.cutout) + '" alt="" loading="lazy">';
          }
          media.innerHTML = imgs;
        }
      });
    }

    _recomputeDerived() {
      var s = this.state;
      var hasSize = !!s.selectedSizeKey;

      var freeShippingActive = false;
      if (hasSize) {
        if (this.shippingRuleMode === 'any_non_large_item_present') {
          freeShippingActive = !s.selectedSizeMerchandise.isLarge || Object.keys(s.selectedUpsells).length > 0;
        } else {
          freeShippingActive = !s.selectedSizeMerchandise.isLarge;
        }
      }
      // Per-quantity-tier override: a merchant can mark a specific quantity option (e.g.
      // "buy 2") to always ship free, even for a size that normally wouldn't qualify on
      // its own (e.g. Large) — this only ever ADDS free shipping, never removes it from a
      // size that already earned it.
      if (s.forceFreeShipping) freeShippingActive = true;
      s.freeShipping = freeShippingActive;

      var pricing = this._calculatePricing();
      this._lastPricing = pricing;
      s.subtotal = pricing.effectiveSubtotal;
      s.compareSubtotal = pricing.originalMerchandiseValue;
      s.itemCount = pricing.itemCount;

      this._updateSizeShippingBadges();
      this._updateSummary();
      this._updateDock();
      var shipStrip = this.querySelector('[data-gax-ship-strip]');
      if (shipStrip) {
        shipStrip.hidden = !s.freeShipping;
        var stripLabel = shipStrip.querySelector('[data-gax-ship-strip-label]');
        if (stripLabel) stripLabel.textContent = this.shippingLabel + ' on this size';
      }
      if (this._debugRender) this._debugRender();
    }

    _formatMoney(cents) {
      var amount = (cents || 0) / 100;
      if (this._moneyFormatter) {
        try { return this._moneyFormatter.format(amount); } catch (e) { /* fall through */ }
      }
      return '$' + amount.toFixed(2);
    }

    _updateSizeShippingBadges() {
      if (this.shippingRuleMode !== 'any_non_large_item_present') return;
      var s = this.state;
      this.sizeCards.forEach(function (card) {
        var badge = card.querySelector('[data-gax-shipping-badge]');
        if (!badge) return;
        if (card.dataset.isLarge === 'true' && Object.keys(s.selectedUpsells).length > 0) {
          badge.hidden = false;
        }
      });
    }

    _updateProgress() {
      var tabs = Array.prototype.slice.call(this.querySelectorAll('[data-gax-step-tab]'));
      var currentIdx = this._stepIndex(this.state.activeStep);
      tabs.forEach(function (tab) {
        var idx = this._stepIndex(tab.dataset.gaxStepTab);
        tab.classList.toggle('is-complete', idx < currentIdx);
        tab.setAttribute('aria-current', tab.dataset.gaxStepTab === this.state.activeStep ? 'step' : 'false');
      }, this);
    }

    /* ---------------- adopt summary (receipt card) ---------------- */

    _updateSummary() {
      var s = this.state;
      var list = this.querySelector('[data-gax-summary-list]');
      var empty = this.querySelector('[data-gax-summary-empty]');
      var shipping = this.querySelector('[data-gax-summary-shipping]');
      var shippingValue = this.querySelector('[data-gax-summary-shipping-value]');
      var breakdown = this.querySelector('[data-gax-summary-breakdown]');
      var totalRow = this.querySelector('[data-gax-summary-total-row]');
      var totalEl = this.querySelector('[data-gax-summary-total]');
      var compareEl = this.querySelector('[data-gax-summary-compare]');
      var savingsPill = this.querySelector('[data-gax-summary-savings-pill]');
      var unlock = this.querySelector('[data-gax-summary-unlock]');
      var finalBtn = this.querySelector('[data-gax-final-cta]');
      var statusEl = this.querySelector('[data-gax-summary-status]');
      if (!list || !empty) return;

      if (!s.selectedSizeKey) {
        empty.hidden = false;
        list.hidden = true;
        if (shipping) shipping.hidden = true;
        if (breakdown) breakdown.hidden = true;
        if (totalRow) totalRow.hidden = true;
        if (savingsPill) savingsPill.hidden = true;
        if (unlock) unlock.hidden = true;
        if (finalBtn) finalBtn.disabled = true;
        return;
      }

      empty.hidden = true;
      list.hidden = false;
      if (totalRow) totalRow.hidden = false;
      if (finalBtn) finalBtn.disabled = false;

      var pricing = this._lastPricing || this._calculatePricing();
      var self = this;

      list.innerHTML = pricing.lines.map(function (line) {
        var thumb = line.image ? '<img class="gax__summary-line-thumb" src="' + escapeHtml(line.image) + '" alt="" loading="lazy">' : '<span class="gax__summary-line-thumb gax__summary-line-thumb--empty"></span>';
        var badge = line.badgeText ? '<span class="gax__summary-line-badge">★ ' + escapeHtml(line.badgeText) + '</span>' : '';
        // Gift pill replaces the generic badge on the reward line.
        if (line.giftBadge) {
          badge = '<span class="gax__summary-line-badge gax__summary-line-badge--gift">🎁 ' + escapeHtml(line.giftBadge) + '</span>';
        }
        var savings = line.totalLineSavings > 0 ? '<span class="gax__summary-line-savings">You save ' + self._formatMoney(line.totalLineSavings) + '</span>' : '';
        var struck = self.showOriginalPrices && line.originalLineTotal > line.effectiveLineTotal ? '<s class="gax__price-original">' + self._formatMoney(line.originalLineTotal) + '</s> ' : '';
        // A fully-discounted gift reads as "FREE" rather than "$0.00".
        var priceText = (line.isGift && line.effectiveLineTotal === 0) ? '<strong class="gax__summary-line-free">FREE</strong>' : self._formatMoney(line.effectiveLineTotal);
        return '<li>' +
          thumb +
          '<span class="gax__summary-line-info"><strong>' + escapeHtml(line.title) + '</strong>' + badge + savings + '</span>' +
          '<span class="gax__summary-line-price"><span>' + struck + priceText + '</span><span class="gax__summary-line-qty">Qty. ' + line.quantity + '</span></span>' +
          '</li>';
      }).join('');

      // Set below whenever shipping resolves to an actual charged dollar amount (not free,
      // not a text label like "Calculated at checkout") — added into the receipt's Total.
      var shippingChargeCents = 0;
      if (shipping) {
        shipping.hidden = false;
        if (shippingValue) {
          if (s.freeShipping) {
            // Show the value being waived struck through beside FREE, when configured.
            shippingValue.innerHTML = pricing.shippingSavings > 0
              ? '<s class="gax__price-original">' + escapeHtml(this._formatMoney(pricing.shippingSavings)) + '</s> FREE'
              : 'FREE';
            shipping.classList.add('is-free');
          } else if (this.largeShippingLabel) {
            // An explicit label (e.g. "Calculated at checkout") always wins when set — it's
            // text, not a dollar figure, so there's nothing numeric to add to the total.
            shippingValue.textContent = this.largeShippingLabel;
            shipping.classList.remove('is-free');
          } else if (this.shippingSavingsCents > 0) {
            // Same configured "what shipping would cost" figure used to show the waived
            // amount above — with no custom label set, it's the actual charge here instead
            // of falling through to a bare "—" with no shipping cost shown at all.
            shippingChargeCents = this.shippingSavingsCents;
            shippingValue.textContent = this._formatMoney(shippingChargeCents);
            shipping.classList.remove('is-free');
          } else {
            shippingValue.textContent = '—';
            shipping.classList.remove('is-free');
          }
        }
      }
      if (unlock) unlock.hidden = !s.freeShipping;

      if (breakdown) {
        if (this.showDetailedReceiptDiscounts && pricing.totalSavingsWithShipping > 0) {
          var rows = [];
          if (this.showReceiptOriginalValue) rows.push(['Original merchandise value', this._formatMoney(pricing.originalMerchandiseValue)]);
          if (this.showReceiptSaleSavings && pricing.productSaleSavings > 0) rows.push(['Product sale savings', '−' + this._formatMoney(pricing.productSaleSavings)]);
          if (this.showReceiptQuantitySavings && pricing.quantityDiscountSavings > 0) rows.push(['Quantity savings', '−' + this._formatMoney(pricing.quantityDiscountSavings)]);
          if (this.showReceiptGiftSavings && pricing.giftSavings > 0) rows.push(['Free gift', '−' + this._formatMoney(pricing.giftSavings)]);
          if (pricing.shippingSavings > 0) rows.push(['Shipping savings', '−' + this._formatMoney(pricing.shippingSavings)]);
          // Every row above is individually toggleable, so a merchant can switch all of
          // them off — leave the box hidden rather than rendering an empty shell.
          breakdown.hidden = rows.length === 0;
          breakdown.innerHTML = rows.map(function (r) {
            return '<div class="gax__summary-breakdown-row"><dt>' + escapeHtml(r[0]) + '</dt><dd>' + escapeHtml(r[1]) + '</dd></div>';
          }).join('');
        } else {
          breakdown.hidden = true;
        }
      }

      // The receipt's displayed Total includes a real, non-zero shipping charge — the
      // merchandise-only s.subtotal underneath it stays untouched since that's what actually
      // matches the cart (shipping isn't a cart line item; it's calculated by Shopify at
      // checkout).
      if (totalEl) totalEl.textContent = this._formatMoney(s.subtotal + shippingChargeCents);
      if (compareEl) {
        if (this.showOriginalPrices && s.compareSubtotal > s.subtotal) {
          // Same shipping charge added to both sides of the strikethrough comparison — it
          // isn't part of the discount being shown off, so it shouldn't inflate the "you
          // save" gap between them.
          compareEl.hidden = false;
          compareEl.textContent = this._formatMoney(s.compareSubtotal + shippingChargeCents);
        } else {
          compareEl.hidden = true;
        }
      }
      if (savingsPill) {
        if (pricing.totalSavingsWithShipping > 0) {
          savingsPill.hidden = false;
          // Headline number includes shipping when the merchant set a shipping value.
          var pillText = 'You saved ' + this._formatMoney(pricing.totalSavingsWithShipping) + '!';
          if (pricing.shippingSavings > 0) pillText += ' (incl. shipping)';
          savingsPill.textContent = pillText;
        } else {
          savingsPill.hidden = true;
        }
      }
      if (statusEl) {
        statusEl.textContent = s.itemCount + ' item' + (s.itemCount === 1 ? '' : 's') + ', ' + this._formatMoney(s.subtotal);
      }
    }

    /* ---------------- dock ---------------- */

    _updateDock() {
      var eyebrow = this.querySelector('[data-gax-dock-eyebrow]');
      var line = this.querySelector('[data-gax-dock-line]');
      var badge = this.querySelector('[data-gax-dock-badge]');
      var shipCell = this.querySelector('[data-gax-dock-shipcell]');
      var shipSub = this.querySelector('[data-gax-dock-shipsub]');
      var totalCell = this.querySelector('[data-gax-dock-totalcell]');
      var total = this.querySelector('[data-gax-dock-total]');
      var continueBtn = this.querySelector('[data-gax-continue]');
      var backBtn = this.querySelector('[data-gax-back]');
      var thumbs = this.querySelector('[data-gax-dock-thumbs]');
      if (!line || !continueBtn) return;

      var s = this.state;
      var step = s.activeStep;
      var qty = s.selectedQuantity || 1;
      var hasSize = !!s.selectedSizeKey;

      if (backBtn) backBtn.hidden = this._stepIndex(step) === 0;
      continueBtn.classList.toggle('is-final', step === 'adopt');

      var thumbSrcs = [];
      if (hasSize && s.selectedSizeMerchandise.cutout) thumbSrcs.push(s.selectedSizeMerchandise.cutout);
      Object.keys(s.selectedUpsells).forEach(function (k) {
        if (s.selectedUpsells[k].cutout) thumbSrcs.push(s.selectedUpsells[k].cutout);
      });
      if (s.rewardSelection && s.rewardSelection.cutout) thumbSrcs.push(s.rewardSelection.cutout);
      if (thumbs) {
        thumbs.innerHTML = thumbSrcs.slice(0, 3).map(function (src) {
          return '<img class="gax__dock-thumb" src="' + escapeHtml(src) + '" alt="" loading="lazy">';
        }).join('');
      }

      if (!hasSize) {
        if (eyebrow) eyebrow.textContent = 'Your adoption';
        line.textContent = 'Choose your Gloomee';
        if (badge) badge.hidden = true;
        if (shipCell) shipCell.hidden = true;
        if (totalCell) totalCell.hidden = true;
        continueBtn.disabled = true;
        continueBtn.innerHTML = 'Continue to quantity <span aria-hidden="true">→</span>';
        return;
      }

      continueBtn.disabled = false;
      var m = s.selectedSizeMerchandise;
      var companionTitles = Object.keys(s.selectedUpsells).map(function (k) { return s.selectedUpsells[k].title; });

      var eyebrowText = 'Your adoption';
      var lineText = m.title;
      var badgeText = '';
      var shipSubText = 'On this size';

      if (step === 'size') {
        eyebrowText = "You're adopting";
        lineText = m.title;
        if (m.recommended && m.badgeText) badgeText = '★ ' + m.badgeText;
        shipSubText = 'On this size';
        continueBtn.innerHTML = 'Continue to quantity <span aria-hidden="true">→</span>';
      } else if (step === 'quantity') {
        eyebrowText = 'Your selection';
        lineText = qty + ' × ' + m.title;
        var selQty = this.quantityCards.filter(function (c) { return c.classList.contains('is-selected'); })[0];
        if (selQty && selQty.dataset.recommended === 'true' && selQty.dataset.badgeText) badgeText = '★ ' + selQty.dataset.badgeText;
        shipSubText = 'On this selection';
        var next = this._nextStep('quantity');
        continueBtn.innerHTML = (next === 'companions' ? 'Continue to companions' : 'Continue to adopt') + ' <span aria-hidden="true">→</span>';
      } else if (step === 'companions') {
        eyebrowText = 'Your crew';
        lineText = qty + ' × ' + m.title + (companionTitles.length ? ' + ' + companionTitles.map(function (t) { return '1 × ' + t; }).join(' + ') : '');
        shipSubText = 'On this order';
        continueBtn.innerHTML = 'Continue to adopt <span aria-hidden="true">→</span>';
      } else if (step === 'adopt') {
        eyebrowText = 'Your crew';
        lineText = qty + ' × ' + m.title + (companionTitles.length ? ' + ' + companionTitles.map(function (t) { return '1 × ' + t; }).join(' + ') : '');
        badgeText = '♥ Adoption complete';
        shipSubText = 'Unlocked';
        continueBtn.innerHTML = 'Adopt the whole crew <span aria-hidden="true">→</span>';
      }

      // A merchant-set badge for this step always wins over the derived one above.
      var customBadge = this.dockBadges && this.dockBadges[step];
      if (customBadge) badgeText = (step === 'adopt' ? '♥ ' : '★ ') + customBadge;

      if (eyebrow) eyebrow.textContent = eyebrowText;
      line.textContent = lineText;
      if (badge) {
        badge.hidden = !badgeText;
        badge.textContent = badgeText;
      }
      if (shipCell) {
        shipCell.hidden = !s.freeShipping;
        if (shipSub) shipSub.textContent = shipSubText;
      }

      var pricing = this._lastPricing || this._calculatePricing();
      if (totalCell) totalCell.hidden = false;
      if (total) {
        var totalHtml = '';
        if (this.showOriginalPrices && pricing.originalMerchandiseValue > s.subtotal) {
          totalHtml += '<s class="gax__price-original">' + this._formatMoney(pricing.originalMerchandiseValue) + '</s> ';
        }
        totalHtml += '<span>' + this._formatMoney(s.subtotal) + '</span>';
        total.innerHTML = totalHtml;
      }
    }

    /* ---------------- cart integration ---------------- */

    _buildAnalyticsSnapshot() {
      var s = this.state;
      return {
        step: s.activeStep,
        sizeKey: s.selectedSizeKey,
        quantity: s.selectedQuantity,
        upsellCount: Object.keys(s.selectedUpsells).length,
        itemCount: s.itemCount,
        subtotalCents: s.subtotal,
      };
    }

    _handleFinalCta() {
      var s = this.state;
      if (!s.selectedSizeKey) return;

      var btn = this.querySelector('[data-gax-final-cta]');
      var dockBtn = this.querySelector('[data-gax-continue]');
      var activeBtn = btn && !btn.disabled ? btn : dockBtn;
      if (!activeBtn || activeBtn.dataset.state === 'loading') return;

      var originalLabel = activeBtn.innerHTML;
      var originalDockLabel = dockBtn ? dockBtn.innerHTML : null;
      activeBtn.dataset.state = 'loading';
      activeBtn.disabled = true;
      if (dockBtn && dockBtn !== activeBtn) dockBtn.disabled = true;
      // Both buttons get the same loading treatment together — the sticky dock bar is
      // what mobile actually shows/taps (always visible, no scrolling to the summary
      // panel needed), so it needs the same cute animation as the panel's own button,
      // regardless of which one was physically clicked. Set on click, not after the fetch
      // resolves, so the animation starts the instant they tap.
      if (this.postAddBehavior === 'checkout') {
        var loadingLabel = 'Rounding up the crew <span class="gax__cta-arrow" aria-hidden="true">→</span>';
        activeBtn.innerHTML = loadingLabel;
        if (dockBtn && dockBtn !== activeBtn) {
          dockBtn.dataset.state = 'loading';
          dockBtn.innerHTML = loadingLabel;
        }
      }



      var items = [];
      items.push({ id: s.selectedSizeMerchandise.variantId, quantity: s.selectedQuantity || 1 });
      Object.keys(s.selectedUpsells).forEach(function (key) {
        items.push({ id: s.selectedUpsells[key].variantId, quantity: 1 });
      });
      if (s.rewardSelection && s.rewardSelection.variantId) {
        items.push({ id: s.rewardSelection.variantId, quantity: 1 });
      }

      var self = this;
      // Sections omitted on the checkout path: that param makes Shopify server-render the
      // cart-drawer/cart-icon-bubble sections into the response, and this path never uses
      // that HTML — it navigates away as soon as the add succeeds. Paying for that render
      // just adds latency to the one response the whole redirect is waiting on.
      var addPayload = { items: items };
      if (this.postAddBehavior !== 'checkout') {
        addPayload.sections = 'cart-drawer,cart-icon-bubble';
        addPayload.sections_url = window.location.pathname;
      }
      fetch(this.cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(addPayload),
      })
        .then(function (response) {
          return response.json().then(function (json) {
            if (!response.ok) {
              var message = (json && (json.description || json.message)) || 'This item is currently unavailable.';
              throw new Error(message);
            }
            return json;
          });
        })
        .then(function (json) {
          self._dispatchAnalytics('gloomee_add_to_cart_success', self._buildAnalyticsSnapshot());

          // Straight to checkout: only reached after /cart/add.js resolved OK, so every
          // item is confirmed in the cart. Skip the drawer swap entirely and keep the
          // button in its loading state through the navigation (no flash of "done", no
          // chance of a double submit).
          if (self.postAddBehavior === 'checkout') {
            // Remembered so _onPageShow can undo exactly this add if the customer hits
            // back from checkout instead of completing it — otherwise clicking "Adopt the
            // whole crew" again just adds these same items a second time on top of what's
            // already sitting in the cart from this attempt (/cart/add.js is additive, not
            // idempotent), doubling everything.
            self._lastCheckoutItems = items;
            var liveNow = self.querySelector('[data-gax-live]');
            if (liveNow) liveNow.textContent = self.cartSuccessMessage;
            window.location.assign(self.checkoutUrl);
            return;
          }

          self._applySectionUpdate(json);

          if (self.scrollToCartAfterAdd) {
            window.scrollTo({ top: 0, behavior: self.isReducedMotion ? 'auto' : 'smooth' });
          }

          var liveRegion = self.querySelector('[data-gax-live]');
          if (liveRegion) liveRegion.textContent = self.cartSuccessMessage;

          window.setTimeout(function () {
            activeBtn.dataset.state = '';
            activeBtn.disabled = false;
            activeBtn.innerHTML = originalLabel;
            if (dockBtn && dockBtn !== activeBtn) dockBtn.disabled = false;
          }, 1600);
        })
        .catch(function (err) {
          self._dispatchAnalytics('gloomee_add_to_cart_error', { message: err.message });
          activeBtn.dataset.state = '';
          activeBtn.disabled = false;
          activeBtn.innerHTML = originalLabel;
          if (dockBtn && dockBtn !== activeBtn) {
            dockBtn.disabled = false;
            dockBtn.dataset.state = '';
            if (originalDockLabel !== null) dockBtn.innerHTML = originalDockLabel;
          }
          var liveRegion = self.querySelector('[data-gax-live]');
          if (liveRegion) liveRegion.textContent = err.message || 'Something went wrong. Please try again.';
        });
    }

    _applySectionUpdate(response) {
      if (!response || !response.sections) return;

      /* Blank-flash fix: hand the fresh section HTML to the theme's own CartDrawer element.
         Its renderContents() swaps `.drawer__inner` + the cart icon bubble via fixParsedHtml,
         clears empty-state classes, re-binds the close button, and only THEN opens the drawer.
         Nothing is cleared before fresh content is ready, so no blank frame can appear. The
         drawer element also carries a server-rendered `is-empty` class when the page loaded
         with an empty cart — remove it, since this update always follows a successful add. */
      var drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.renderContents === 'function') {
        try {
          drawer.classList.remove('is-empty');
          drawer.renderContents(response, !this._shouldOpenDrawer());
          return;
        } catch (e) {
          if (this.debugMode) console.warn('[gax] theme renderContents failed, using fallback', e);
        }
      }

      // Fallback: manual section swap (content first, open last — never an empty drawer).
      var cartDrawerHtml = response.sections['cart-drawer'];
      var cartIconHtml = response.sections['cart-icon-bubble'];

      if (cartDrawerHtml) {
        var drawerInner = document.querySelector('cart-drawer .drawer__inner, #CartDrawer .drawer__inner');
        if (drawerInner) {
          var parsed = new DOMParser().parseFromString(cartDrawerHtml, 'text/html');
          var newInner = parsed.querySelector('.drawer__inner');
          if (newInner) {
            drawerInner.innerHTML = newInner.innerHTML;
            var outer = drawerInner.closest('cart-drawer');
            if (outer) outer.classList.remove('is-empty');
          }
        }
      }

      if (cartIconHtml) {
        var iconBubble = document.getElementById('cart-icon-bubble');
        if (iconBubble) {
          var parsedIcon = new DOMParser().parseFromString(cartIconHtml, 'text/html');
          var newIconInner = parsedIcon.body.firstElementChild;
          iconBubble.innerHTML = newIconInner ? newIconInner.innerHTML : cartIconHtml;
        }
      }

      if (this._shouldOpenDrawer()) {
        var fallbackDrawer = document.querySelector('cart-drawer');
        if (fallbackDrawer && typeof fallbackDrawer.open === 'function') {
          fallbackDrawer.open();
        } else {
          var cartIcon = document.querySelector('#cart-icon-bubble, a[href*="/cart"], .header__icon--cart');
          if (cartIcon) cartIcon.click();
        }
      }
    }

    /* The drawer opens only when the merchant's post-add behavior asks for it; the
       legacy checkbox still applies within that mode. */
    _shouldOpenDrawer() {
      if (this.postAddBehavior === 'stay' || this.postAddBehavior === 'checkout') return false;
      return this.openCartAfterAdd;
    }

    /* ---------------- theme editor lifecycle ---------------- */

    _setupThemeEditorHooks() {
      if (!this.isEditorMode) return;
      var self = this;
      var allCards = this.sizeCards.concat(this.quantityCards, this.companionCards);

      document.addEventListener('shopify:block:select', function (event) {
        var card = allCards.filter(function (el) { return el === event.target || el.contains(event.target); })[0];
        var pane = card ? card.closest('[data-gax-pane]') : (self.panes.filter(function (p) { return p === event.target || p.contains(event.target); })[0]);
        if (!pane) return;
        var step = pane.dataset.gaxPane;
        if (step !== self.state.activeStep) {
          var dir = self._stepIndex(step) > self._stepIndex(self.state.activeStep) ? 'forward' : 'back';
          self.state.isTransitioning = false;
          self._activatePane(step, { direction: dir });
        }
      });

      document.addEventListener('shopify:section:reorder', function () {
        self.state.isTransitioning = false;
        self._measureChromeOffset();
        self._renderAllScenes();
      });

      document.addEventListener('shopify:section:load', function () {
        self._measureChromeOffset();
        self._renderAllScenes();
      });
    }

    /* ---------------- debug ---------------- */

    _setupDebugPanel() {
      var panel = document.createElement('div');
      panel.className = 'gax__debug';
      this.appendChild(panel);
      var self = this;
      this._debugRender = function () {
        panel.textContent = JSON.stringify({
          activeStep: self.state.activeStep,
          selectedSizeKey: self.state.selectedSizeKey,
          selectedQuantity: self.state.selectedQuantity,
          upsells: Object.keys(self.state.selectedUpsells),
          freeShipping: self.state.freeShipping,
          subtotal: self._formatMoney(self.state.subtotal),
          itemCount: self.state.itemCount,
          sceneBp: self._sceneBreakpoint(),
          sceneStates: self._sceneStateCandidates(self.state.activeStep),
        }, null, 2);
      };
      this._debugRender();
    }

    _dispatchAnalytics(eventName, detail) {
      if (!this.analyticsEnabled) return;
      var payload = detail || {};
      payload.namespace = this.analyticsNamespace;
      document.dispatchEvent(new CustomEvent(eventName, { detail: payload, bubbles: true }));
    }
  }

  customElements.define('gloomee-adoption-experience', GloomeeAdoptionExperience);
})();
